import React, { useCallback, useEffect, useState } from "react";
import { Archive, Banknote, ChevronRight, Copy, Pencil, Plus, ArrowLeft, ArrowLeftRight, Eye, EyeOff, Gift, Loader2, Receipt, Split } from "lucide-react";
import { api, argent, ouvrirPdf } from "./api";
import ExportEleves, { BoutonExport } from "./ExportEleves";
import { Tableau, Cellule, Badge, Eyebrow, Puces, Section, Modale, ChampTexte, ChampChoix, BoutonSombre, Recherche } from "./ui";
import { Bloc, SelectLigne } from "./Liste";
import { useConfirmation } from "./Confirmation";
import { BadgeStatut, ModaleMontants, ModaleEchelonnement, DetailJournal, QuiJournal } from "./Facturation";
import { apercuFusion, useChoixFusion, AVerifierFusion } from "./FusionFiches";
import { ModaleEncaisser } from "./Caisse";
import { RondFiche, aUnePhoto, usePhotosOuvertes } from "./PhotoEleve";

const MOTIFS = [
  "", "Transfert vers un autre cours (audition, réorientation)",
  "Déménagement", "Horaire qui ne convient plus", "Raisons financières",
  "Blessure ou raison médicale", "Changement d'activité", "L'enfant ne s'y plaît plus",
  "Raisons familiales", "Insatisfaction (cours, prof, organisation)", "Autre",
];
const DROITS_IMAGE = ["", "Oui", "Non"];

/* Une inscription qui compte encore (ni annulée, ni désinscrite, ni en liste d'attente). */
const encoreActive = (i) => !/^(annul|désinscrit|desinscrit|liste)/i.test(String(i.statut || ""));
const jourFr = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join(".") : "");
// Le dernier changement de cours de cette ligne, s'il vient d'une fusion de cours
// (Planning → « Fusionner deux cours ») : l'ancien cours et le jour de la fusion. Une
// fusion annulée plus tard n'en est plus une (cf. `fusionAnnulee`).
const fusionDe = (i) => {
  const p = (i.cours_precedents || []).slice(-1)[0];
  return p && /fusion/i.test(String(p.motif || "")) && !/annul/i.test(String(p.motif || "")) ? p : null;
};
// Fusion annulée quelques jours après : l'élève est revenu dans ce cours, la dernière
// entrée dit où il a été entre-temps.
const fusionAnnulee = (i) => {
  const p = (i.cours_precedents || []).slice(-1)[0];
  return p && /fusion annul/i.test(String(p.motif || "")) ? p : null;
};
// « Hip-hop 7-9 ans, mardi 17:00–18:00, Claudio » : l'ancien cours tel qu'il était, pour
// le reconnaître même quand deux groupes portent le même nom (école, 09.10.2026).
const coursAvant = (p) => [
  p.cours_nom,
  [String(p.jour || "").toLowerCase(), p.heure].filter(Boolean).join(" "),
  p.professeur,
].filter(Boolean).join(", ");


/* « Changé de cours » : DIT par l'école (statut « Changé de cours », ou motif « Transfert
   vers un autre cours » posé avant), plus jamais deviné — une carte 10 cours annulée puis
   un Dancehall au semestre s'affichait « Changé de cours → Dancehall » (09.10.2026). */
const estCarte10 = (i) => /carte\s*10/i.test(String(i.formule || ""));
const changeDeCours = (i) => !encoreActive(i) && !estCarte10(i) && (i.changement_de_cours != null
  ? !!i.changement_de_cours : /^transfert/i.test(String(i.motif_desinscription || "").trim()));
/* Le cours qui a pris sa place : celui que l'école a choisi (« Changer de cours » vers un
   cours que l'élève suivait déjà), sinon le cours du même semestre arrivé après, actif. */
const remplacePar = (i, toutes) => (!changeDeCours(i) ? null
  : i.change_vers ? { cours_nom: i.change_vers } : toutes.find(
  (a) => a.id !== i.id && a.cours_id !== i.cours_id && !estCarte10(a)
    && String(a.semestre || "").slice(-1) === String(i.semestre || "").slice(-1)
    && encoreActive(a) && String(a.horodatage || "") > String(i.horodatage || "")));
/* Une ligne déjà QUITTÉE (annulée ou désinscrite) : il n'y a plus de cours à déplacer. */
const ligneQuittee = (i) => /^(annul|désinscrit|desinscrit)/i.test(String(i.statut || ""));
/* Désinscrit mais suit encore son cours jusqu'à la fin du semestre payé (place conservée, pas
   « ne vient plus ») : la ligne reste sur la fiche jusque-là, même retirée avant (école,
   02.11.2026 : un élève toujours sur un cours du samedi n'avait « aucune inscription »). */
const encoreSuivie = (i) => /^(désinscrit|desinscrit)/i.test(String(i.statut || ""))
  && !i.ne_vient_plus_le && !!i.place_conservee_jusqu_au
  && String(i.place_conservee_jusqu_au).slice(0, 10) >= new Date().toISOString().slice(0, 10);
/* On ne change de cours que depuis une ligne en cours (inscrit ou en liste d'attente) :
   le bouton sur une ligne annulée ou déjà « Changé de cours » prêtait à confusion. */
const peutChangerDeCours = (i) => (!ligneQuittee(i) || encoreSuivie(i)) && !changeDeCours(i);
/* Une ligne en cours qu'on peut « retirer » comme ajoutée par erreur : une liste d'attente
   (jamais facturée), ou un cours ajouté par l'école dans la console. Une inscription venue
   du site se quitte par « Modifier » (Annulé, Désinscrit : date et motif comptent). */
const retirableParErreur = (i) => !ligneQuittee(i) && (/^liste/i.test(String(i.statut || ""))
  || String(i.type_demande || "").toLowerCase().includes("console"));

export const age = (naissance) => {
  const d = new Date(String(naissance || "").slice(0, 10));
  if (Number.isNaN(d.getTime())) return "";
  const a = new Date();
  let n = a.getFullYear() - d.getFullYear();
  if (a.getMonth() < d.getMonth() || (a.getMonth() === d.getMonth() && a.getDate() < d.getDate())) n -= 1;
  return n;
};
const estAdulte = (naissance) => age(naissance) !== "" && age(naissance) >= 18;
/* La date de naissance n'est obligatoire que pour les enfants : un adulte s'inscrit en
   cochant « réservation pour moi-même », qui remplit le champ `adulte`. Sans aucune des
   deux informations, on n'affiche rien plutôt qu'un badge faux. */
const categorieAge = (el) => {
  if (String(el.adulte || "").toLowerCase().startsWith("oui")) return "Adulte";
  if (age(el.date_naissance) === "") return "";
  return estAdulte(el.date_naissance) ? "Adulte" : "Enfant";
};

const STATUTS = ["Inscrit", "Liste d'attente", "Annulé", "Désinscrit"];
/* La formule de l'inscription. « Carte 10 cours » : prix fixe, encaissée en espèces,
   valable jusqu'à la fin du semestre, sans réduction d'accumulation ni de fratrie. */
const FORMULES = ["", "Cours individuels", "Carte 10 cours", "Infinity Pass Enfant",
  "Golden Pass Enfant", "Infinity Pass Adulte"];
const TON_STATUT = { Inscrit: "vert", "Liste d'attente": "ambre", Annulé: "rouge", Désinscrit: "bleu" };

const PAGE_ELEVES = 50;

export default function Eleves({ cible }) {
  const [q, setQ] = useState("");
  const [vue, setVue] = useState("actifs");
  const [data, setData] = useState({ total: 0, resultats: [] });
  const [fiche, setFiche] = useState(null);
  const { demander, informer } = useConfirmation();
  const { choisir, fenetre: fenetreFusion } = useChoixFusion();
  const [fusion, setFusion] = useState(null);
  const [occupe, setOccupe] = useState("");
  const [export_, setExport_] = useState(false);
  /* Combien de fiches afficher : la liste annonçait « N résultat(s) » mais n'en montrait
     que 50, sans moyen d'aller plus loin. « Afficher plus » en charge 50 de plus. */
  const [limite, setLimite] = useState(PAGE_ELEVES);
  useEffect(() => { setLimite(PAGE_ELEVES); }, [q, vue]);

  const charger = useCallback(() => {
    const filtres = vue === "archives" ? "&archives=1" : vue === "doublons" ? "&doublons=1" : "";
    return api(`/eleves?q=${encodeURIComponent(q)}${filtres}&limite=${limite}`).then(setData).catch(() => {});
  }, [q, vue, limite]);

  useEffect(() => {
    const t = setTimeout(charger, 250);
    return () => clearTimeout(t);
  }, [charger]);

  const ouvrir = (id) => api(`/eleves/${encodeURIComponent(id)}`).then(setFiche).catch(() => {});
  // « doublons:Nola Gros » depuis l'écran À traiter : on montre les DEUX fiches côte à
  // côte dans la liste (filtre « Doublons possibles » + nom cherché) au lieu d'ouvrir
  // l'une des deux au hasard.
  useEffect(() => {
    if (!cible) return;
    const vise = /^doublons:(.+)$/.exec(cible);
    if (vise) {
      setVue("doublons");
      setQ(vise[1]);
    } else {
      ouvrir(cible);
    }
  }, [cible]);

  const desarchiver = async (e) => {
    await api(`/eleves/${encodeURIComponent(e.id)}/desarchiver`, { method: "POST" });
    await charger();
  };

  // Toutes les paires évidentes d'un coup : c'est le chemin le plus court pour l'école.
  const fusionnerTout = async () => {
    /* La règle réelle de l'école : même prénom, même nom (mots même inversés) ET même date
       de naissance — y compris sous DEUX familles (parents séparés, deux adresses). La
       fenêtre disait « même famille » : elle montre maintenant ces paires-là à part et
       demande, comme « À traiter » (« Fusionner quand même »), avant de les réunir. */
    let apercu;
    try {
      apercu = await api("/eleves/doublons/fusionner-tout/apercu");
    } catch (e) {
      await informer({ titre: "Fusion impossible", ton: "danger", texte: e.message });
      return;
    }
    const paires = apercu.paires || [];
    if (!paires.length) {
      await informer({
        titre: "Aucun doublon évident",
        texte: "Aucun enfant n'a deux fiches de même nom et même date de naissance.",
        points: (apercu.a_trancher || []).map((d) => `${d.eleve} : ${d.pourquoi}`),
      });
      return;
    }
    const jour = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join(".") : "");
    const famille = (f) => [f?.responsable, f?.email].filter(Boolean).join(", ") || "famille sans nom";
    const deuxFamilles = paires.filter((p) => p.deux_familles);
    if (!await demander({
      titre: "Fusionner les doublons évidents",
      texte: "Pour chaque enfant en double (même prénom, même nom — mots même inversés — et même "
        + "date de naissance), je garde la fiche qui porte les cours et je rattache tout le reste dessus.\n"
        + `${apercu.nb_meme_famille} paire(s) dans la même famille`
        + (deuxFamilles.length ? `, ${deuxFamilles.length} sous deux familles différentes (je te les montre ensuite).` : "."),
      points: ["La fiche en trop est archivée, jamais effacée : elle se remet d'un clic.",
        "Si deux lignes existent pour le même cours, je garde la plus complète et j'écarte l'autre en doublon.",
        "Si les deux fiches ne disent pas la même chose (n° AVS, droit à l'image…), la fiche gardée garde sa valeur et affiche « à vérifier » avec celle de l'autre.",
        "Un enfant avec trois fiches ou plus n'est pas touché : je te le signale."],
      valider: deuxFamilles.length ? "Continuer" : "Fusionner",
    })) return;
    let avecDeuxFamilles = false;
    if (deuxFamilles.length) {
      avecDeuxFamilles = await demander({
        titre: `${deuxFamilles.length} enfant(s) sous deux familles différentes`,
        ton: "attention",
        texte: "Même nom et même date de naissance, mais deux fiches famille (souvent deux parents, "
          + "deux adresses). La fusion réunit l'enfant ET relie les deux adresses : chaque parent "
          + "le verra. Les fusionner aussi ?",
        points: deuxFamilles.map((p) => `${p.eleve}${p.date_naissance ? ` (né·e le ${jour(p.date_naissance)})` : ""} : `
          + `${famille(p.famille_gardee)} — et — ${famille(p.famille_absorbee)}`),
        valider: "Oui, les fusionner aussi",
        annuler: apercu.nb_meme_famille ? "Non, seulement la même famille" : "Non, ne pas les fusionner",
      });
      if (!avecDeuxFamilles && !apercu.nb_meme_famille) return;
    }
    setOccupe("fusion-tout");
    try {
      const r = await api("/eleves/doublons/fusionner-tout", {
        method: "POST", body: JSON.stringify({ deux_familles: avecDeuxFamilles }),
      });
      setFusion(null);
      await charger();
      await informer({
        titre: `${r.fusionnees} fiche(s) fusionnée(s)`, ton: "succes", texte: r.message,
        points: [...r.detail.map((d) => `${d.eleve} : ${d.lignes_deplacees} ligne(s) rattachée(s)`
          + (d.doublons_ecartes ? `, ${d.doublons_ecartes} inscription(s) en double écartée(s)` : "")
          + (d.a_verifier?.length ? ` — à vérifier sur la fiche : ${d.a_verifier.join(", ")}` : "")),
        ...r.a_trancher.map((d) => `${d.eleve} : ${d.pourquoi}`)],
      });
    } catch (e) {
      await informer({ titre: "Fusion impossible", ton: "danger", texte: e.message });
    }
    setOccupe("");
  };

  // Fusion de deux fiches du même enfant : on garde celle qui a les inscriptions actives.
  // L'aperçu (rien n'est écrit) dit d'abord si les deux fiches se contredisent (nom, date
  // de naissance, AVS…) : la fenêtre propose alors de choisir la bonne valeur.
  const fusionner = async (garder, absorber) => {
    let apercu;
    try {
      apercu = await apercuFusion({ garder: garder.id, absorber: absorber.id });
    } catch (err) {
      await informer({ titre: "Fusion impossible", ton: "danger", texte: err.message });
      return;
    }
    let choix = null;
    if (apercu.conflits?.length) {
      choix = await choisir({
        apercu, titre: `Fusionner les deux fiches de ${garder.prenom} ${garder.nom}`,
        sous_titre: `Fiche gardée : ${garder.responsable || garder.email || garder.id}`,
      });
      if (!choix) return;
    } else if (!await demander({
      titre: `Fusionner les deux fiches de ${garder.prenom} ${garder.nom}`,
      ton: "attention",
      texte: `Tout ce qui est rattaché à la fiche de ${absorber.responsable || absorber.email} sera rattaché à la fiche de ${garder.responsable || garder.email}.`,
      points: ["Inscriptions, essais, stages, auditions et parrainages sont déplacés.",
        "La fiche en trop est archivée, jamais effacée — elle reste dans le filtre « Archivés »."],
      valider: "Fusionner",
    })) return;
    try {
      const r = await api("/eleves/fusionner", {
        method: "POST",
        body: JSON.stringify({ garder: garder.id, absorber: absorber.id, ...(choix ? { choix } : {}) }),
      });
      setFusion(null);
      await charger();
      await informer({ titre: "Fiches fusionnées", ton: "succes", texte: r.message });
    } catch (err) {
      await informer({ titre: "Fusion impossible", ton: "danger", texte: err.message });
    }
  };

  if (fiche) return <FicheEleve fiche={fiche} onFermer={() => { setFiche(null); charger(); }} onRecharger={() => ouvrir(fiche.eleve.id)} />;

  return (
    <div className="space-y-5" data-testid="console-eleves">
      {export_ && <ExportEleves onFermer={() => setExport_(false)} />}
      {fenetreFusion}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Eyebrow>{data.total} résultat(s)</Eyebrow>
          <h1 className="font-display text-3xl tracking-tight mt-1">Élèves</h1>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <BoutonExport onClick={() => setExport_(true)} />
          <Recherche valeur={q} onChange={setQ} largeur={340} testid="console-eleves-search"
            placeholder="Nom de l'élève, du parent, e-mail…" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Puces testid="eleves-vues" valeur={vue}
          options={[{ valeur: "actifs", label: "Fiches actives" },
            { valeur: "doublons", label: "Doublons possibles", nb: data.nb_doublons ?? 0 },
            { valeur: "archives", label: "Archivés", nb: data.nb_archives ?? 0 }]}
          onChange={(v) => { setVue(v); setFusion(null); }} />
        <span className="text-xs text-[var(--muted)]">
          {vue === "doublons"
            ? "Le même enfant sur deux fiches : souvent deux adresses de parents, ou le prénom et le nom inversés dans le formulaire (« Nola Gros » et « Gros Nola »). Choisis la fiche à garder — la recherche accepte les mots dans n'importe quel ordre. Si les deux fiches ne disent pas la même chose (nom, date de naissance…), la fusion te demande quoi garder."
            : vue === "archives"
              ? "Fiches sorties de la liste (doublons, fiches vides). Rien n'a été effacé : un clic les remet."
              : "Les fiches archivées ne sont pas affichées ici."}
        </span>
      </div>

      {vue === "doublons" && (data.nb_doublons ?? 0) > 0 && (
        <div className="rounded-2xl px-5 py-4 text-sm flex flex-wrap items-center gap-3"
          style={{ background: "#ECFDF5", border: "1px solid #A7F3D0" }}
          data-testid="eleves-fusion-tout">
          <div className="flex-1 min-w-[260px]">
            <strong>Le plus simple :</strong> je garde la fiche qui porte les cours, je rattache
            tout le reste dessus, j'archive l'autre et j'écarte les inscriptions en double.
            Rien n'est effacé, tout se remet d'un clic.
          </div>
          <BoutonSombre onClick={fusionnerTout} disabled={occupe === "fusion-tout"}
            testid="eleves-fusionner-tout">
            {occupe === "fusion-tout" ? <Loader2 size={14} className="animate-spin" /> : <Split size={14} />}
            Fusionner les doublons évidents
          </BoutonSombre>
        </div>
      )}

      {fusion && (
        <div className="rounded-2xl px-5 py-4 text-sm" data-testid="eleves-fusion-barre"
          style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1E3A8A" }}>
          <strong>{fusion.prenom} {fusion.nom}</strong> — fiche de {fusion.responsable || fusion.email} choisie.
          Clique maintenant sur « Fusionner ici » de la fiche à <strong>garder</strong>.
          <button className="btn-ghost ml-3" data-testid="eleves-fusion-annuler" onClick={() => setFusion(null)}>
            Annuler
          </button>
        </div>
      )}

      <Tableau
        testid="tableau-eleves"
        onClic={(e) => ouvrir(e.id)}
        entetes={["Élève", "Responsable", "E-mail", "Cours", "AVS", ""]}
        lignes={data.resultats}
        rendu={(e) => (
          <>
            <Cellule fort>
              {e.prenom} {e.nom}
              {(e.statut_membre && e.statut_membre !== "Membre") && (
                <div className="text-xs font-normal text-[var(--muted)]">{e.statut_membre}</div>
              )}
              {e.archive && (
                <div className="text-xs font-normal" style={{ color: "#B45309" }}>
                  archivée — {e.archive_motif || "doublon"}
                </div>
              )}
            </Cellule>
            <Cellule>{e.responsable || "—"}</Cellule>
            <Cellule muet>{e.email}</Cellule>
            <Cellule>
              {e.nb_cours > 0 ? e.cours.join(", ")
                : e.cours_quittes?.length ? (
                  /* Désinscrite mais encore là jusqu'à la fin du semestre : on nomme son
                     cours et son dernier jour, au lieu d'un « essai seulement » trompeur. */
                  <span data-testid="eleve-cours-quittes">
                    {e.cours_quittes.map((c) => c.nom).join(", ")}
                    <span className="text-[var(--muted)]">
                      {" — "}désinscrit·e
                      {e.cours_quittes[0].dernier_cours_le
                        ? `, dernier cours le ${jourFr(e.cours_quittes[0].dernier_cours_le)}`
                        : e.cours_quittes[0].le ? ` le ${jourFr(e.cours_quittes[0].le)}` : ""}
                    </span>
                  </span>
                ) : e.nb_essais > 0
                  ? <span className="text-[var(--muted)]">essai seulement ({e.nb_essais})</span>
                  : <span className="text-[var(--muted)]">aucun cours</span>}
            </Cellule>
            <Cellule>{e.avs ? <Badge ton="vert">renseigné</Badge> : <Badge ton="ambre">manquant</Badge>}</Cellule>
            <Cellule>
              <div className="flex items-center gap-1.5" onClick={(ev) => ev.stopPropagation()}>
                {vue === "archives" && (
                  <button className="btn-ghost" data-testid="eleve-desarchiver"
                    onClick={() => desarchiver(e)}>
                    Remettre dans la liste
                  </button>
                )}
                {vue === "doublons" && (fusion ? (
                  fusion.id !== e.id ? (
                    <button className="btn-ghost" data-testid="eleve-fusionner-ici"
                      onClick={() => fusionner(e, fusion)}>
                      <Split size={13} /> Fusionner ici
                    </button>
                  ) : <span className="text-xs text-[var(--muted)]">fiche choisie</span>
                ) : (
                  <button className="btn-ghost" data-testid="eleve-choisir-fusion"
                    onClick={() => setFusion(e)}>
                    <Copy size={13} /> Fusionner cette fiche…
                  </button>
                ))}
                <ChevronRight size={16} className="text-[var(--muted)]" />
              </div>
            </Cellule>
          </>
        )}
      />
      {data.total > (data.resultats || []).length && (
        <div className="flex items-center justify-center gap-3 text-sm" data-testid="eleves-pagination">
          <span className="text-[var(--muted)]">
            {(data.resultats || []).length} affiché(s) sur {data.total}
          </span>
          <button className="btn-ghost" data-testid="eleves-afficher-plus"
            onClick={() => setLimite((n) => n + PAGE_ELEVES)}>
            Afficher plus
          </button>
          <button className="btn-ghost" data-testid="eleves-afficher-tout"
            onClick={() => setLimite(data.total)}>
            Tout afficher
          </button>
        </div>
      )}
    </div>
  );
}

function FicheEleve({ fiche, onFermer, onRecharger }) {
  const { eleve, famille, inscriptions, essais, fratrie, parrainage, source, stages = [], auditions = [], spectacles = [], workshops = [], options } = fiche;
  const [modale, setModale] = useState(null);
  const { demander, informer } = useConfirmation();
  /* La modale se ferme dès que l'enregistrement a réussi : son message (liste d'attente,
     facture à corriger…) disparaissait avec elle. Il est donc redit APRÈS la fermeture. */
  const apresModale = (titre) => async (message) => {
    setModale(null);
    onRecharger();
    if (message) await informer({ titre, ton: "succes", texte: message });
  };
  const [voirMasquees, setVoirMasquees] = useState(false);
  const [occupeLigne, setOccupeLigne] = useState("");
  // La photo ou l'avatar choisi par la famille dans son app (chantier « photos_eleves »).
  const photosOuvertes = usePhotosOuvertes();
  // Ses cartes 10 cours (Jen, 30.10.2026 : « je ne vois pas sur les élèves où en sont leurs
  // cartes ») : où en est chaque carte (version de test : carte numérique) et son paiement.
  const [cartes, setCartes] = useState(null);
  const aDesCartes = (inscriptions || []).some(estCarte10);
  useEffect(() => {
    if (!aDesCartes) { setCartes(null); return; }
    api(`/carte-10-numerique?eleve=${encodeURIComponent(eleve.id)}`).then(setCartes).catch(() => setCartes(null));
  }, [eleve.id, aDesCartes, inscriptions]);
  const carteDe = (i) => (cartes?.cartes || []).find((c) => c.inscription_id === i.id);

  // Les inscriptions retirées de la fiche (doublons, annulations) : elles encombraient la
  // liste et l'école ne savait plus laquelle comptait.
  const masquees = (inscriptions || []).filter((i) => i.masque_fiche && !encoreSuivie(i));
  const visibles = (inscriptions || []).filter((i) => !i.masque_fiche || encoreSuivie(i));

  const libererPlace = async (i, annuler) => {
    if (!annuler && !await demander({
      titre: "Libérer la place",
      texte: `${eleve.prenom || "L'élève"} ne vient plus au cours « ${i.cours_nom || ""} » : dès aujourd'hui, il sort `
        + "des listes de présence, de l'app du prof et du quota du cours (la place peut se revendre). "
        + "Sa facture et son historique ne changent pas.",
      valider: "Libérer la place",
    })) return;
    setOccupeLigne(i.id);
    try {
      await api(`/inscriptions/${encodeURIComponent(i.id)}/liberer-la-place`,
        { method: "POST", body: JSON.stringify({ annuler }) });
      onRecharger();
    } catch (e) {
      await informer({ titre: "Impossible", ton: "attention", texte: String(e.message || e) });
    } finally {
      setOccupeLigne("");
    }
  };

  /* « Retirer de la fiche » demande d'abord confirmation (école, 02.11.2026 : une ligne
     retirée par erreur faisait croire qu'un élève n'avait plus de cours). */
  const retirerDeLaFiche = async (i) => {
    if (!await demander({
      titre: "Retirer cette ligne de la fiche ?",
      texte: `« ${i.cours_nom} » (${i.statut || "Inscrit"}) ne s'affichera plus sur la fiche de l'élève.`,
      points: ["À utiliser pour une ligne annulée, en double, ou un élève qui ne suit plus ce cours.",
        "Rien n'est effacé : « Afficher les lignes retirées » la montre encore, et l'historique garde qui l'a retirée et quand.",
        "Les listes de présence et la facture ne changent pas."],
      valider: "Retirer de la fiche",
      annuler: "Garder la ligne",
    })) return;
    await masquer(i, true);
  };

  const masquer = async (i, masquer_) => {
    setOccupeLigne(i.id);
    try {
      await api(`/inscriptions/${encodeURIComponent(i.id)}/masquer`,
        { method: "POST", body: JSON.stringify({ masquer: masquer_ }) });
      onRecharger();
    } catch (e) {
      await informer({ titre: "Impossible", ton: "attention", texte: String(e.message || e) });
    } finally {
      setOccupeLigne("");
    }
  };

  /* Une ligne EN COURS ajoutée par erreur (liste d'attente, ou cours ajouté par l'école) :
     « Retirer de la fiche » s'affichait sur la liste d'attente mais le serveur refusait
     (« encore active… ») — « il marche pas » (école, 09.10.2026). Le bouton dit maintenant
     ce qu'il va faire, l'annule après confirmation, puis la retire. */
  const retirerParErreur = async (i) => {
    const attente = /^liste/i.test(String(i.statut || ""));
    let facture = "Rien n'est facturé : une ligne en liste d'attente ne l'est jamais, aucune facture ne change.";
    if (!attente) {
      // L'aperçu du serveur (rien n'est écrit) : la facture est-elle déjà partie ?
      const apercu = await api(`/inscriptions/${encodeURIComponent(i.id)}/impact?statut=${encodeURIComponent("Annulé")}`)
        .catch(() => null);
      facture = !apercu
        ? "Facture : si elle est déjà partie, elle passe dans « À corriger » ; rien ne repart sans ta validation."
        : (apercu.changements || []).some((c) => c.recalcul_requis)
          ? `Facture déjà envoyée : elle passe dans « À corriger » (${i.montant_annonce || 0} CHF de ce cours en moins) ; rien ne repart sans ta validation.`
          : "Pas encore facturé : le cours sort simplement du calcul, aucune facture ne change.";
    }
    if (!await demander({
      titre: "Retirer cette ligne ajoutée par erreur ?",
      texte: `« ${i.cours_nom} » (${i.statut || "Inscrit"}) sera annulée et sortira de la fiche.`,
      points: [facture,
        attente ? "Sa place dans la liste d'attente du cours est libérée : les élèves suivants avancent."
          : "Sa place est rendue au cours.",
        "Rien n'est effacé : « Afficher les lignes retirées » la montre encore (« Annulé »), l'historique la garde.",
        "Aucun e-mail ne part à la famille."],
      valider: "Retirer la ligne",
      annuler: "Garder la ligne",
    })) return;
    setOccupeLigne(i.id);
    try {
      const r = await api(`/inscriptions/${encodeURIComponent(i.id)}/masquer`,
        { method: "POST", body: JSON.stringify({ masquer: true, annuler: true }) });
      onRecharger();
      await informer({ titre: "Ligne retirée", ton: "succes", texte: r.message });
    } catch (e) {
      await informer({ titre: "La ligne n'a pas été retirée", ton: "danger", texte: String(e.message || e) });
    } finally {
      setOccupeLigne("");
    }
  };

  /* « Retirer la photo » : l'école enlève la photo (ou l'avatar) choisie par la famille,
     après confirmation. La famille retrouve la lettre et peut en remettre une. */
  const retirerPhoto = async () => {
    const quoi = eleve.photo_maj ? "la photo" : "l'avatar";
    if (!await demander({
      titre: `Retirer ${quoi} de ${eleve.prenom} ?`,
      texte: `${eleve.photo_maj ? "Cette photo a été choisie" : "Cet avatar a été choisi"} par la famille dans son app.`,
      points: ["La lettre reprend sa place, ici et dans l'app de la famille.",
        eleve.photo_maj ? "La photo est effacée du serveur. La famille pourra en remettre une."
          : "La famille pourra en choisir un autre.",
        `L'historique de la fiche garde qui l'a ${eleve.photo_maj ? "retirée" : "retiré"} et quand.`],
      valider: eleve.photo_maj ? "Retirer la photo" : "Retirer l'avatar",
      annuler: "Garder",
    })) return;
    try {
      await api(`/eleves/${encodeURIComponent(eleve.id)}/photo`, { method: "DELETE" });
      onRecharger();
    } catch (e) {
      await informer({ titre: "Impossible de retirer", ton: "danger", texte: String(e.message || e) });
    }
  };

  // Fiche en trop (doublon, fiche vide) : on la sort de la liste sans rien effacer.
  const archiver = async () => {
    const actives = (inscriptions || []).filter(
      (i) => !String(i.statut || "").toLowerCase().startsWith("annul"));
    if (!await demander({
      titre: `Archiver la fiche de ${eleve.prenom} ${eleve.nom}`,
      ton: actives.length ? "attention" : "info",
      texte: actives.length
        ? `Attention : cette fiche a encore ${actives.length} inscription(s) active(s).`
        : "Cette fiche n'a aucune inscription active.",
      points: ["Elle n'apparaîtra plus dans la liste des élèves ni dans les recherches.",
        "Elle reste consultable dans le filtre « Archivés » et se remet d'un clic.",
        "Rien n'est effacé : elle est seulement rangée de côté."],
      valider: "Archiver la fiche",
    })) return;
    try {
      const r = await api(`/eleves/${encodeURIComponent(eleve.id)}/archiver`,
        { method: "POST", body: JSON.stringify({ motif: "" }) });
      await informer({ titre: "Fiche archivée", ton: "succes", texte: r.message });
      onFermer();
    } catch (e) {
      await informer({ titre: "Impossible d'archiver", ton: "danger", texte: e.message });
    }
  };


  return (
    <div className="space-y-6" data-testid="console-fiche-eleve">
      <button onClick={onFermer} className="btn-ghost"><ArrowLeft size={15} /> Retour aux élèves</button>

      <div className="bg-white rounded-2xl p-6 border" style={{ borderColor: "var(--border)" }}>
        <div className="flex flex-wrap items-start gap-3">
          {/* La photo ou l'avatar choisi par la famille (jamais pour une fiche archivée). */}
          <RondFiche eleve={eleve} taille={64} />
          <div>
            <Eyebrow>Fiche élève</Eyebrow>
            <h1 className="font-display text-3xl tracking-tight mt-1">{eleve.prenom} {eleve.nom}</h1>
            <div className="mt-2 flex flex-wrap gap-2">
              {categorieAge(eleve) ? (
                <Badge ton={categorieAge(eleve) === "Adulte" ? "bleu" : "vert"} testid="eleve-badge-age">
                  {categorieAge(eleve)}{age(eleve.date_naissance) !== "" ? ` · ${age(eleve.date_naissance)} ans` : ""}
                </Badge>
              ) : (
                <Badge ton="bleu" testid="eleve-badge-age">Âge non renseigné</Badge>
              )}
              {eleve.statut_membre && eleve.statut_membre !== "Membre" && <Badge ton="ambre">{eleve.statut_membre}</Badge>}
              <Badge ton={(eleve.droit_image || "").toLowerCase().startsWith("o") ? "vert" : "rouge"}>
                Droit à l'image : {eleve.droit_image || "non renseigné"}
              </Badge>
              {(eleve.fusion_a_verifier || []).length > 0 && (
                <Badge ton="ambre" testid="eleve-badge-fusion">à vérifier après fusion</Badge>
              )}
            </div>
          </div>
          <div className="ml-auto flex flex-wrap gap-2">
            <button className="btn-primary" data-testid="eleve-modifier" onClick={() => setModale({ quoi: "fiche" })}>
              <Pencil size={14} /> Modifier la fiche
            </button>
            {aUnePhoto(eleve, photosOuvertes) && (
              <button className="btn-ghost" data-testid="eleve-retirer-photo" onClick={retirerPhoto}>
                {eleve.photo_maj ? "Retirer la photo" : "Retirer l'avatar"}
              </button>
            )}
            {eleve.archive ? (
              <button className="btn-ghost" data-testid="eleve-fiche-desarchiver"
                onClick={async () => {
                  await api(`/eleves/${encodeURIComponent(eleve.id)}/desarchiver`, { method: "POST" });
                  await onRecharger();
                }}>
                Remettre dans la liste
              </button>
            ) : null}
          </div>
        </div>
        {/* Une fusion faite sans fenêtre de choix a gardé une valeur sur deux : on dit
            laquelle était ailleurs, jusqu'à ce que l'école tranche. */}
        <AVerifierFusion eleve={eleve} onFait={onRecharger}
          onModifier={() => setModale({ quoi: "fiche" })} />
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-5 text-sm">
          <Champ label="Membre depuis" valeur={eleve.membre_depuis
            ? `${String(eleve.membre_depuis).slice(0, 10).split("-").reverse().join(".")} — première inscription ou essai`
            : "date inconnue"} />
          {/* La trace d'un départ : annoncé quand, dernier cours quand, et pourquoi. */}
          {eleve.plus_membre_depuis && (
            /* Tant que le semestre payé court, l'élève suit encore ses cours et compte dans
               les quotas : la fiche dit « membre jusqu'au », pas « plus membre depuis ». */
            <Champ
              label={eleve.encore_membre_jusqu_au ? "Membre jusqu'au" : "Plus membre depuis"}
              testid="eleve-plus-membre"
              valeur={(eleve.encore_membre_jusqu_au
                ? `${String(eleve.encore_membre_jusqu_au).split("-").reverse().join(".")} — départ annoncé le ${String(eleve.plus_membre_depuis).split("-").reverse().join(".")}`
                : `${String(eleve.plus_membre_depuis).split("-").reverse().join(".")}`
                  + (eleve.dernier_cours_le
                    ? ` — dernier cours le ${String(eleve.dernier_cours_le).split("-").reverse().join(".")}`
                    : ""))
                + (eleve.motif_depart ? ` · ${eleve.motif_depart}` : "")} />
          )}
          <Champ label="Date de naissance" valeur={/^\d{4}-\d{2}-\d{2}$/.test(String(eleve.date_naissance || ""))
            ? String(eleve.date_naissance).split("-").reverse().join(".") : eleve.date_naissance} />
          {/* Réponse du formulaire du site : « Comment as-tu connu 1000 ARTS ? » */}
          <Champ label="Nous a connus par" valeur={source?.canal
            ? `${source.canal}${source.precision ? ` — ${source.precision}` : ""}`
            : "non renseigné"} />
          <Champ label="N° AVS" valeur={eleve.avs || "manquant"} alerte={!eleve.avs} />
          <Champ label="Statut de membre" testid="eleve-statut-membre"
            valeur={`${eleve.statut_membre || "Membre"} — ${eleve.encore_membre_jusqu_au
              ? "il suit ses cours et compte dans les quotas jusque-là"
              : "suit les inscriptions"}`} />
          <Champ label="Droit à l'image" valeur={eleve.droit_image || "non renseigné"} alerte={!eleve.droit_image} />
          <Champ label="Responsable" valeur={`${famille?.prenom_responsable || ""} ${famille?.nom_responsable || ""}`.trim()} />
          <Champ label="E-mail" valeur={famille?.email} />
          {(famille?.emails_secondaires || []).length > 0 && (
            <Champ label="Autre adresse de la famille"
              valeur={`${famille.emails_secondaires.join(", ")} — même famille, une seule facture`} />
          )}
          <Champ label="Téléphone" valeur={famille?.telephone} />
          <Champ label="Adresse" valeur={`${famille?.adresse || ""}, ${famille?.npa || ""} ${famille?.localite || ""}`} />
          {fratrie.length > 0 && <Champ label="Fratrie" valeur={fratrie.map((f) => `${f.prenom} ${f.nom}`).join(", ")} />}
          <Champ
            label="Parrainé·e par"
            valeur={parrainage?.parraine_par
              ? `${parrainage.parraine_par}${parrainage.parrain_reconnu && parrainage.parrain_reconnu !== parrainage.parraine_par ? ` — reconnu : ${parrainage.parrain_reconnu}` : ""}`
              : "—"}
          />
          {/* Une ligne par parrainage (le serveur réunit une décision et la ligne qu'elle a
              produite). Seul un parrainage pas encore décidé est « à trancher », en orange :
              attribué, écarté ou annulé, c'est tranché (école, 30.10.2026). */}
          {(parrainage?.decisions || []).map((d, k) => (
            <Champ key={k} label="Décision sur ce parrainage"
              alerte={!["auto", "attribue", "ignore", "annule"].includes(d.statut)}
              valeur={d.statut === "auto"
                ? `bon de ${d.montant} CHF accordé à ${d.parrain}${d.a_la_main ? " (attribué à la main)" : ""}`
                : d.statut === "attribue" ? `attribué à la main à ${d.parrain}`
                : d.statut === "ignore" ? `écarté — ${d.motif || "sans raison notée"}`
                : d.statut === "annule" ? "annulé par l'école"
                : `à trancher — ${d.motif || ""}`} />
          ))}
          {parrainage?.filleuls?.length > 0 && (
            <Champ
              label={`A parrainé (${parrainage.filleuls.length})`}
              valeur={`${parrainage.filleuls.map((f) => f.nom).join(", ")} — ${parrainage.filleuls.reduce((s, f) => s + f.montant, 0)} CHF déduits de la facture`}
            />
          )}
        </div>
      </div>

      <Section
        titre={<span>Inscriptions <span className="text-sm text-[var(--muted)]">({visibles.length})</span></span>}
        aide="Inscrit par défaut · Liste d'attente quand le cours est plein · Annulé avant le début · Désinscrit en cours de semestre. Les pastilles « règlement non accepté » et « sans signature » viennent du formulaire d'inscription en ligne (colonnes « Règlement accepté » et « Signature électronique ») ; un cours ajouté ici par l'école n'en a pas. Les inscriptions en double sont repérées automatiquement et remontent dans « À traiter → Doublons d'inscription » : la ligne en trop s'y annule en un clic. Une ligne annulée se retire ensuite de la fiche avec « Retirer de la fiche » — elle n'est jamais effacée, elle sort juste de la liste. Une ligne ajoutée par erreur (liste d'attente, ou « ajouté par l'école ») se retire aussi avec ce bouton : elle est d'abord annulée, après confirmation."
        testid="section-inscriptions"
        action={
          <div className="flex flex-wrap gap-1.5">
            {masquees.length > 0 && (
              <button className="btn-ghost" data-testid="eleve-voir-masquees"
                onClick={() => setVoirMasquees((v) => !v)}>
                <EyeOff size={13} /> {voirMasquees
                  ? "Cacher les lignes retirées"
                  : `Afficher ${masquees.length} ligne(s) retirée(s)`}
              </button>
            )}
            <button className="btn-primary" data-testid="eleve-ajouter-cours" onClick={() => setModale({ quoi: "ajout" })}>
              <Plus size={15} /> Ajouter un cours
            </button>
          </div>
        }
      >
        <Tableau
          testid="eleve-inscriptions"
          entetes={["Cours", "Jour", "Heure", "Semestre", "Statut", { label: "Montant", droite: true }, "Parrainé par", ""]}
          lignes={voirMasquees ? inscriptions : visibles}
          vide="Aucune inscription semestrielle."
          rendu={(i) => (
            <>
              <Cellule fort>{i.cours_nom}
                <div className="mt-1 flex flex-wrap gap-1">
                  {(i.adulte || "").toLowerCase().startsWith("oui") && <Badge ton="bleu">adulte</Badge>}
                  {/^carte 10/i.test(String(i.formule || "")) && (
                    <span title="Carte 10 cours : 10 séances valables jusqu'à la fin du semestre, payées au comptoir ou sur la facture du semestre. Le détail : Facturation › Cartes 10 cours.">
                      <Badge ton="ambre" testid={`eleve-carte10-${i.id}`}>
                        {cartes?.numerique && carteDe(i) ? `carte 10 · ${carteDe(i).faites}/${carteDe(i).nb}` : "carte 10 cours"}
                      </Badge>
                    </span>
                  )}
                  {/pass/i.test(String(i.formule || "")) && <Badge ton="bleu">{i.formule}</Badge>}
                  {fusionDe(i) && (
                    <span title={`Cours fusionné le ${jourFr(fusionDe(i).jusqu_au)} (Planning → Fusionner deux cours) : avant, ${coursAvant(fusionDe(i))}${fusionDe(i).salle ? ` (${fusionDe(i).salle})` : ""}.`}>
                      <Badge ton="bleu" testid="inscription-fusion">fusion de cours</Badge>
                    </span>
                  )}
                  {(i.audition_validee || "").toLowerCase().startsWith("oui") && <Badge ton="vert">audition validée</Badge>}
                  {carteDe(i) && <LigneCarteDix c={carteDe(i)} numerique={cartes.numerique} />}
                  {String(i.type_demande || "").toLowerCase().includes("console") ? (
                    <span title="Cours ajouté par l'école dans la console : il n'y a pas eu de formulaire en ligne, donc ni case « règlement accepté » ni signature électronique.">
                      <Badge ton="bleu">ajouté par l'école</Badge>
                    </span>
                  ) : (
                    <>
                      {!(i.reglement_accepte || "").toLowerCase().startsWith("oui") && (
                        <span title="Vient du formulaire d'inscription en ligne : la famille n'a pas coché la case du règlement.">
                          <Badge ton="rouge">règlement non accepté</Badge>
                        </span>
                      )}
                      {!(i.signature || "").trim() && (
                        <span title="Vient du formulaire d'inscription en ligne : aucun nom n'a été signé.">
                          <Badge ton="ambre">sans signature</Badge>
                        </span>
                      )}
                    </>
                  )}
                </div>
                {fusionDe(i) ? (
                  <div className="text-xs font-normal text-[var(--muted)] mt-1" data-testid="inscription-fusion-avant">
                    fusion de cours le {jourFr(fusionDe(i).jusqu_au)} · avant : {coursAvant(fusionDe(i))}
                  </div>
                ) : fusionAnnulee(i) ? (
                  <div className="text-xs font-normal text-[var(--muted)] mt-1" data-testid="inscription-fusion-annulee">
                    fusion annulée le {jourFr(fusionAnnulee(i).jusqu_au)} · de retour dans ce cours (entre-temps : {coursAvant(fusionAnnulee(i))})
                  </div>
                ) : (i.cours_precedents || []).length > 0 && (
                  <div className="text-xs font-normal text-[var(--muted)] mt-1" data-testid="inscription-change-avant">
                    changé de cours le {jourFr(i.cours_precedents.slice(-1)[0].jusqu_au)} · avant : {coursAvant(i.cours_precedents.slice(-1)[0])}
                  </div>
                )}
              </Cellule>
              <Cellule>{i.jour}</Cellule>
              <Cellule>{i.heure}</Cellule>
              <Cellule muet>{i.semestre}</Cellule>
              <Cellule>
                {changeDeCours(i) ? (
                  <span title={`Statut réel : ${i.statut || "Annulé"} — l'élève n'a pas quitté l'école, il suit un autre cours.`}>
                    <Badge ton="bleu">Changé de cours</Badge>
                    {remplacePar(i, inscriptions) && (
                      <div className="text-xs mt-1 font-semibold" style={{ color: "#0F766E" }}
                        data-testid="inscription-change-vers">
                        → {remplacePar(i, inscriptions).cours_nom}
                      </div>
                    )}
                  </span>
                ) : (
                  <Badge ton={TON_STATUT[i.statut] || "vert"}>{i.statut || "Inscrit"}</Badge>
                )}
                {i.doublon && <div className="text-xs mt-1" style={{ color: "#B45309" }}>doublon</div>}
                {!encoreActive(i) && (i.motif_desinscription || "").trim() && (
                  <div className="text-xs text-[var(--muted)] mt-1" data-testid="inscription-motif">
                    motif : {i.motif_desinscription}
                  </div>
                )}
                {i.desinscription_le && (
                  <div className="text-xs text-[var(--muted)] mt-1">
                    annoncé le {String(i.desinscription_le).slice(0, 10).split("-").reverse().join(".")}
                    {i.place_conservee_jusqu_au && !i.ne_vient_plus_le
                      && ` · suit jusqu'au ${String(i.place_conservee_jusqu_au).slice(0, 10).split("-").reverse().join(".")}`}
                  </div>
                )}
                {/* « Libérer la place » (école, 30.10.2026) : la personne ne vient plus avant
                    son dernier cours payé — hors listes et quota, facture inchangée. */}
                {i.ne_vient_plus_le ? (
                  <div className="text-xs mt-1" style={{ color: "#B45309" }} data-testid="inscription-ne-vient-plus">
                    place libérée · ne vient plus depuis le {String(i.ne_vient_plus_le).slice(0, 10).split("-").reverse().join(".")}
                    {" · "}<button className="underline" disabled={occupeLigne === i.id}
                      onClick={() => libererPlace(i, true)}>annuler</button>
                  </div>
                ) : !encoreActive(i) && i.place_conservee_jusqu_au
                  && String(i.place_conservee_jusqu_au).slice(0, 10) >= new Date().toISOString().slice(0, 10) && (
                  <button className="btn-ghost text-xs mt-1" disabled={occupeLigne === i.id}
                    onClick={() => libererPlace(i, false)} data-testid="inscription-liberer-place">
                    Libérer la place (ne vient plus)
                  </button>
                )}
              </Cellule>
              <Cellule droite>
                {i.montant_annonce ? `${i.montant_annonce} CHF` : i.prix_inconnu ? (
                  /* Cours que la console ne sait pas chiffrer (À traiter › « Cours sans prix ») :
                     « Modifier » propose d'écrire le prix. */
                  <span className="text-xs font-semibold" style={{ color: "#B91C1C" }}
                    title={i.prix_inconnu_raison || ""} data-testid="inscription-sans-prix">sans prix</span>
                ) : "—"}
              </Cellule>
              <Cellule muet>{i.parraine_par || "—"}</Cellule>
              <Cellule>
                <div className="flex flex-wrap gap-1.5">
                  <button className="btn-ghost" data-testid="inscription-modifier"
                    onClick={() => setModale({ quoi: "inscription", inscription: i })}>
                    <Pencil size={13} /> Modifier
                  </button>
                  {peutChangerDeCours(i) && (
                    <button className="btn-ghost" data-testid="inscription-changer-cours"
                      onClick={() => setModale({ quoi: "changement", inscription: i })}>
                      <ArrowLeftRight size={13} /> Changer de cours
                    </button>
                  )}
                  {/* Les doublons sont repérés tout seuls : ils remontent dans « À traiter
                      → Doublons d'inscription », où la ligne en trop s'annule en un clic.
                      Ici : le retrait d'une ligne DÉJÀ annulée, ou d'une ligne ajoutée par
                      erreur (annulée d'abord, après confirmation). */}
                  {i.masque_fiche && !encoreSuivie(i) ? (
                    <button className="btn-ghost" data-testid="inscription-remettre"
                      disabled={occupeLigne === i.id} onClick={() => masquer(i, false)}>
                      <Eye size={13} /> Remettre dans la liste
                    </button>
                  ) : ligneQuittee(i) && !encoreSuivie(i) ? (
                    <button className="btn-ghost" data-testid="inscription-masquer"
                      title="Retire la ligne de la fiche. Elle reste en base, au journal et dans la vue « Annulées / doublons »."
                      disabled={occupeLigne === i.id} onClick={() => retirerDeLaFiche(i)}>
                      <EyeOff size={13} /> Retirer de la fiche
                    </button>
                  ) : retirableParErreur(i) ? (
                    <button className="btn-ghost" data-testid="inscription-retirer-erreur"
                      title="Ligne ajoutée par erreur : elle est annulée (après confirmation), puis retirée de la fiche."
                      disabled={occupeLigne === i.id} onClick={() => retirerParErreur(i)}>
                      <EyeOff size={13} /> Retirer de la fiche
                    </button>
                  ) : null}
                </div>
              </Cellule>
            </>
          )}
        />
      </Section>

      {essais.length > 0 && (
        <BlocEssais essais={essais} cours={options?.cours || []} onRecharger={onRecharger} />
      )}

      {stages.length > 0 && (
        <BlocParticipations
          titre="Stages" famille="stages" lignes={stages} testid="eleve-stages"
          aide="Le groupe et le statut se règlent ici comme dans la Liste : « Annulé » sort l'enfant de la feuille du stage."
          colonnes={[["stage", "Stage"], ["dates_stage", "Dates"], ["montant", "Montant"]]}
          selects={[["groupe", "Groupe", options?.groupes_stage || []], ["statut", "Statut", options?.statuts || []]]}
        />
      )}

      {workshops.length > 0 && (
        <BlocParticipations
          titre="Workshops heels" famille="workshops" lignes={workshops} testid="eleve-workshops"
          aide="Les workshops du samedi : aucune facture n'est émise, le paiement se fait au comptoir (TWINT ou espèces). « Encaisser » : la même fenêtre que Facturation › Cash / espèces (tout ou une partie)."
          colonnes={[["seances_txt", "Workshops choisis"]]}
          paiement={{ label: "Paiement", rendu: (l) => <PaiementWorkshop l={l} onFait={onRecharger} /> }}
          selects={[["statut", "Statut", options?.statuts || []]]}
        />
      )}

      {auditions.length > 0 && (
        <BlocParticipations
          titre="Auditions" famille="auditions" lignes={auditions} testid="eleve-auditions"
          aide="Le résultat noté à la main sur la feuille d'audition se saisit ici, et se lit ensuite sur cette fiche."
          colonnes={[["audition", "Audition"], ["groupe_vise", "Groupe visé"], ["categorie", "Catégorie"]]}
          selects={[["statut", "Statut", options?.statuts || []], ["resultat", "Résultat", options?.resultats_audition || []]]}
        />
      )}

      {spectacles.length > 0 && (
        <BlocParticipations
          titre="Spectacles" famille="spectacles" lignes={spectacles} testid="eleve-spectacles"
          aide="Les places réservées par la famille pour un spectacle. La réservation est souvent prise au nom du parent : elle est rattachée par l'adresse e-mail de la famille."
          colonnes={[["spectacle", "Spectacle"], ["date_spectacle", "Date"],
            ["nb_personnes", "Personnes"], ["prenom", "Réservé par"]]}
          selects={[["statut", "Statut", options?.statuts_spectacle || []]]}
        />
      )}

      <FacturesEleve eleveId={eleve.id} onRecharger={onRecharger} />

      {/* Après la facturation, avant l'historique : un bon cadeau se crée deux fois par
          an, il n'a pas à occuper le haut de la fiche (école, 05.10.2026). */}
      <BonsCadeaux eleveId={eleve.id} eleve={`${eleve.prenom} ${eleve.nom}`.trim()} />

      <HistoriqueEleve eleveId={eleve.id} />

      {modale?.quoi === "fiche" && (
        <FormulaireFiche eleve={eleve} famille={famille} onArchiver={eleve.archive ? null : archiver}
          onFermer={() => setModale(null)}
          onOk={() => { setModale(null); onRecharger(); }} />
      )}
      {modale?.quoi === "inscription" && (
        <FormulaireInscription inscription={modale.inscription} inscriptions={inscriptions || []}
          onFermer={() => setModale(null)}
          onOk={apresModale("Changement de cours noté")} />
      )}
      {modale?.quoi === "changement" && (
        <FormulaireChangementCours inscription={modale.inscription} eleve={eleve}
          inscriptions={inscriptions || []}
          onFermer={() => setModale(null)} onOk={apresModale("Cours changé")} />
      )}
      {modale?.quoi === "ajout" && (
        <FormulaireAjout eleve={eleve} onFermer={() => setModale(null)} onOk={apresModale("Cours ajouté")} />
      )}
    </div>
  );
}

/* Sous une inscription « Carte 10 cours » de la fiche : où en est la carte (version de test :
   carte numérique) et comment elle se paie. */
function LigneCarteDix({ c, numerique }) {
  const p = c.paiement || {};
  const fin = c.fin ? c.fin.split("-").reverse().join(".") : "";
  const paiement = p.mode === "facture" ? `sur la facture${p.statut_facture ? ` (${p.statut_facture.toLowerCase()})` : ""}`
    : p.montant == null ? "au comptoir" : Number(p.reste || 0) <= 0.02 ? "encaissée au comptoir"
      : `au comptoir · reste ${argent(p.reste)}`;
  return (
    <div className="basis-full text-[11px] text-[var(--muted)] font-normal mt-0.5" data-testid={`eleve-carte10-detail-${c.inscription_id}`}>
      {numerique && `${c.faites}/${c.nb} séances · ${c.statut === "terminee" ? "terminée" : c.statut === "expiree" ? "expirée" : `${c.restantes} restante${c.restantes > 1 ? "s" : ""}`} · `}
      {fin && `valable jusqu'au ${fin} · `}{paiement}
    </div>
  );
}

/* Tout ce qui a bougé sur cette fiche : l'élève, ses inscriptions, ses essais, ses
   factures. Replié par défaut — on l'ouvre quand une famille contexte un changement. */
function HistoriqueEleve({ eleveId }) {
  const [items, setItems] = useState(null);
  const [ouvert, setOuvert] = useState(false);

  // Relu à chaque ouverture : un changement fait entre-temps (fusion de cours au Planning,
  // facture corrigée…) doit y figurer sans recharger toute la fiche.
  useEffect(() => {
    if (!ouvert) return undefined;
    let vivant = true;
    setItems(null);
    api(`/eleves/${encodeURIComponent(eleveId)}/journal?limite=150`)
      .then((d) => { if (vivant) setItems(d.items); }).catch(() => { if (vivant) setItems([]); });
    return () => { vivant = false; };
  }, [ouvert, eleveId]);

  const quand = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso
      : d.toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", year: "2-digit",
        hour: "2-digit", minute: "2-digit" });
  };

  return (
    <Section titre="Historique de cette fiche" testid="section-historique-eleve"
      aide="Qui a changé quoi sur cet élève : ses inscriptions (changement de cours, fusion de cours et son annulation), ses cours d'essai, ses absences annoncées, les e-mails envoyés à la famille et ses factures — de la plus récente à la plus ancienne. Rien ne s'efface."
      action={
        <button className="btn-ghost" data-testid="eleve-historique-ouvrir"
          onClick={() => setOuvert(!ouvert)}>
          {ouvert ? "Masquer" : "Voir l'historique"}
        </button>
      }>
      {!ouvert ? (
        <p className="text-sm text-[var(--muted)]">
          L'historique se charge à la demande : cliquez sur « Voir l'historique ».
        </p>
      ) : !items ? (
        <p className="text-sm text-[var(--muted)]">Chargement de l'historique…</p>
      ) : (
        <Tableau
          testid="eleve-historique"
          entetes={["Quand", "Qui", "Quoi", "Ce qui a changé"]}
          lignes={items.map((i, n) => ({ ...i, id: `${i.le}-${n}` }))}
          vide="Aucune modification enregistrée sur cette fiche."
          rendu={(i) => (
            <>
              <Cellule muet>{quand(i.le)}</Cellule>
              <Cellule><QuiJournal ligne={i} /></Cellule>
              <Cellule>
                <div className="leading-snug">{i.action}</div>
                <div className="text-xs text-[var(--muted)] mt-1 break-words">
                  {i.quoi}{i.concerne_sous ? ` · ${i.concerne_sous}` : ""}
                </div>
              </Cellule>
              <Cellule muet><DetailJournal detail={i.detail} /></Cellule>
            </>
          )}
        />
      )}
    </Section>
  );
}

/* Les factures de la famille, lues depuis le moteur de facturation : même
   statuts, même modale de correction — la fiche élève et l'onglet Facturation
   parlent de la même chose. */
/* Les bons cadeaux offerts par l'école : montant libre, validité d'un an par défaut, et
   déduction automatique de la PROCHAINE inscription (jamais de la facture en cours). */
function BonsCadeaux({ eleveId, eleve }) {
  const [data, setData] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [montant, setMontant] = useState("");
  const [motif, setMotif] = useState("");
  const [jusqu, setJusqu] = useState("");
  const [occupe, setOccupe] = useState(false);
  const { demander, informer } = useConfirmation();

  const charger = React.useCallback(() => {
    api(`/eleves/${encodeURIComponent(eleveId)}/bons-cadeaux`)
      .then(setData).catch(() => setData({ items: [], disponible: 0 }));
  }, [eleveId]);
  useEffect(charger, [charger]);

  const offrir = async () => {
    setOccupe(true);
    try {
      const r = await api(`/eleves/${encodeURIComponent(eleveId)}/bons-cadeaux`, {
        method: "POST",
        body: JSON.stringify({ montant: Number(montant), motif, valide_jusqu: jusqu }),
      });
      setOuvert(false); setMontant(""); setMotif(""); setJusqu("");
      charger();
      await informer({ titre: "Bon cadeau enregistré", ton: "succes", texte: r.message });
    } catch (e) {
      await informer({ titre: "Impossible", ton: "attention", texte: String(e.message || e) });
    } finally {
      setOccupe(false);
    }
  };

  const annuler = async (b) => {
    if (!await demander({
      titre: `Annuler le bon de ${b.montant} CHF`,
      ton: "attention",
      texte: "Le bon ne sera plus déduit d'aucune facture.",
      points: ["Il reste visible ici, barré, avec la date de son annulation."],
      valider: "Annuler ce bon",
    })) return;
    try {
      await api(`/bons-cadeaux/${encodeURIComponent(b.id)}/annuler`,
        { method: "POST", body: JSON.stringify({ motif: "" }) });
      charger();
    } catch (e) {
      await informer({ titre: "Impossible", ton: "attention", texte: String(e.message || e) });
    }
  };

  if (!data) return null;

  return (
    <Section
      titre={<span>Bons cadeaux <span className="text-sm text-[var(--muted)]">({data.items.length})</span></span>}
      aide="L'école décide du montant. Le bon se déduit tout seul de la PROCHAINE inscription — pas de la facture en cours, pour ne pas rouvrir une facture déjà calculée. Validité d'un an par défaut."
      testid="section-bons-cadeaux"
      action={
        <button className="btn-primary" data-testid="bon-cadeau-ajouter" onClick={() => setOuvert(true)}>
          <Gift size={15} /> Offrir un bon
        </button>
      }
    >
      {data.disponible > 0 && (
        <div className="text-sm mb-3" style={{ color: "#15803D" }} data-testid="bon-cadeau-disponible">
          {data.disponible} CHF à déduire de la prochaine inscription.
        </div>
      )}
      <Tableau
        testid="eleve-bons-cadeaux"
        entetes={[{ label: "Montant", droite: true }, "Motif", "Valable jusqu'au", "État", ""]}
        lignes={data.items}
        vide="Aucun bon cadeau pour cet élève."
        rendu={(b) => (
          <>
            <Cellule droite fort>{b.montant} CHF</Cellule>
            <Cellule muet>{b.motif || "—"}</Cellule>
            <Cellule>{jourFr(b.valide_jusqu)}</Cellule>
            <Cellule>
              {b.annule_le ? <Badge ton="rouge">annulé</Badge>
                : b.utilise_le ? <Badge ton="bleu">déduit</Badge>
                  : b.echu ? <Badge ton="ambre">échu</Badge>
                    : <Badge ton="vert">à déduire</Badge>}
              {b.utilise_le && (
                <div className="text-xs text-[var(--muted)] mt-1">
                  facture {b.facture || "—"} · {jourFr(b.utilise_le)}
                </div>
              )}
            </Cellule>
            <Cellule>
              {!b.utilise_le && !b.annule_le && (
                <button className="btn-ghost" data-testid={`bon-cadeau-annuler-${b.id}`}
                  onClick={() => annuler(b)}>Annuler</button>
              )}
            </Cellule>
          </>
        )}
      />
      {ouvert && (
        <Modale titre={`Offrir un bon cadeau à ${eleve}`} onFermer={() => setOuvert(false)}
          testid="modale-bon-cadeau">
          <div className="grid sm:grid-cols-2 gap-4">
            <ChampTexte label="Montant du bon (CHF)" valeur={montant} type="number"
              onChange={setMontant} testid="bon-cadeau-montant" />
            <ChampTexte label="Valable jusqu'au" valeur={jusqu} type="date" onChange={setJusqu}
              aide="Un an à compter d'aujourd'hui si tu laisses vide." testid="bon-cadeau-jusqu" />
          </div>
          <ChampTexte label="Motif (figure sur la facture)" valeur={motif} onChange={setMotif}
            aide="Par exemple : bon cadeau d'anniversaire, concours gagné, geste commercial."
            testid="bon-cadeau-motif" />
          <ul className="text-xs text-[var(--muted)] list-disc pl-5 space-y-1">
            <li>Le bon se déduit de la prochaine inscription, pas de la facture en cours.</li>
            <li>Il apparaît sur la facture sous la ligne « Bon cadeau ».</li>
            <li>Une fois déduit, il ne revient jamais sur une autre facture.</li>
          </ul>
          <div className="mt-5 flex justify-end">
            <BoutonSombre onClick={offrir} disabled={occupe || !(Number(montant) > 0)}
              testid="bon-cadeau-enregistrer">
              {occupe ? "…" : "Enregistrer le bon"}
            </BoutonSombre>
          </div>
        </Modale>
      )}
    </Section>
  );
}

function FacturesEleve({ eleveId, onRecharger }) {
  const [data, setData] = useState(null);
  const [modale, setModale] = useState(null);

  const charger = React.useCallback(() => {
    api(`/facturation/eleve/${encodeURIComponent(eleveId)}`).then(setData)
      .catch((e) => setData({ factures: [], erreur: e.message || "erreur" }));
  }, [eleveId]);
  useEffect(charger, [charger]);

  /* Les factures se recalculent à l'ouverture de la fiche : la section se montre tout de
     suite, avec « en cours de chargement », pour qu'on ne croie pas qu'il n'y en a aucune
     (école, 09.10.2026). Un échec le dit, au lieu d'afficher « Aucune facture ». */
  if (!data || data.erreur) {
    return (
      <Section titre="Factures" testid="section-factures-eleve">
        {!data ? (
          <div className="flex items-center gap-2 text-sm text-[var(--muted)] py-3" data-testid="eleve-factures-chargement">
            <Loader2 size={14} className="animate-spin" />
            Chargement des factures de la famille…
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3 text-sm py-3" data-testid="eleve-factures-erreur">
            <span style={{ color: "#B91C1C" }}>Les factures n'ont pas pu être chargées.</span>
            <button className="btn-ghost" onClick={() => { setData(null); charger(); }}>Réessayer</button>
          </div>
        )}
      </Section>
    );
  }

  /* Les factures de stage suivent celles des cours, dans le même tableau (école,
     09.10.2026 : « la facture de stage ici aussi »). Ce sont les lignes de
     Facturation › Stages : elles se lisent ici, elles se modifient et s'encaissent là-bas. */
  const lignes = [...data.factures.map((f) => ({ ...f, id: `${f.semestre}-${f.numero}` })),
    ...(data.stages || []).map((f) => ({ ...f, id: `stage-${f.cle}` }))];

  return (
    <Section titre={<span>Factures <span className="text-sm text-[var(--muted)]">({lignes.length})</span></span>}
      aide="Les montants se corrigent ici comme dans l'onglet Facturation : c'est la même donnée. Les factures de stage (n° F-…ST-…) se modifient et s'encaissent dans Facturation › Stages : « Ouvrir dans Facturation »."
      testid="section-factures-eleve">
      <Tableau
        testid="eleve-factures"
        entetes={["Semestre / stage", "N°", { label: "Total", droite: true }, { label: "Reçu", droite: true }, "Statut", ""]}
        lignes={lignes}
        vide="Aucune facture pour cette famille."
        rendu={(f) => (f.genre === "stage" ? <CellulesFactureStage f={f} famille={data.famille} /> : (
          <>
            <Cellule fort>Semestre {f.semestre}<div className="text-xs font-normal text-[var(--muted)]">{f.semestre_txt}</div></Cellule>
            <Cellule muet>{f.numero || "—"}
              {f.cle && f.cle !== data.famille && (
                <div className="text-xs mt-1" data-testid="eleve-facture-autre-email">facturée à {f.cle}</div>
              )}
            </Cellule>
            <Cellule droite fort>{f.total ? `${f.total} CHF` : "—"}</Cellule>
            <Cellule droite muet>{f.recu ? `${f.recu} CHF` : "—"}</Cellule>
            <Cellule>
              <BadgeStatut statut={f.statut} />
              {f.envoyee_le && <div className="text-xs text-[var(--muted)] mt-1">envoyée le {jourFr(f.envoyee_le)}</div>}
              {f.renvoi && (
                <div className="text-xs mt-1" style={{ color: "#B45309" }} data-testid="eleve-facture-renvoi">
                  Corrigée — à renvoyer : la famille a reçu la version d'avant{f.renvoi.total ? ` (${f.renvoi.total} CHF)` : ""}{f.renvoi.le ? ` le ${f.renvoi.le.split("-").reverse().join(".")}` : ""}
                </div>
              )}
              {f.echelonnement_nb > 0 && (
                <div className="text-xs mt-1" style={{ color: "#1D4ED8" }} data-testid="eleve-facture-echelonnement">
                  paiement en {f.echelonnement_nb}× · {(f.echelonnement_dates || []).map((d) => d.split("-").reverse().join(".")).join(", ")}
                </div>
              )}
              {f.motif && <div className="text-xs text-[var(--muted)] mt-1">{f.motif}</div>}
              {f.parrainages?.length > 0 && (
                <div className="text-xs mt-1" style={{ color: "#15803D" }} data-testid="eleve-facture-parrainage">
                  Parrainage déduit : {f.parrainages.map((p) => `${p.montant} CHF (${p.filleul})`).join(", ")}
                  {f.parrainage_fige ? ` — figé sur la facture ${f.pdf_numero}${f.genere_le ? ` du ${f.genere_le}` : ""}` : " — sur cette facture"}
                </div>
              )}
              {f.parrainages_reportes?.length > 0 && (
                <div className="text-xs mt-1" style={{ color: "#B45309" }} data-testid="eleve-facture-parrainage-reporte">
                  Parrainage à créditer : {f.parrainages_reportes.map((p) => `${p.montant} CHF (${p.filleul})`).join(", ")}
                  {" "}— la facture était déjà partie
                </div>
              )}
              {f.parrainage_a_la_main && (
                <div className="text-xs text-[var(--muted)] mt-1">Parrainage déjà passé à la main dans l'ajustement</div>
              )}
            </Cellule>
            <Cellule>
              <div className="flex gap-1.5">
                <button className="btn-ghost" data-testid="eleve-facture-modifier" onClick={() => setModale({ quoi: "montants", facture: f })}>
                  <Pencil size={13} /> Modifier
                </button>
                <button className="btn-ghost" data-testid="eleve-facture-echelonner" onClick={() => setModale({ quoi: "echelon", facture: f })}>
                  <Split size={13} /> {f.echelonnement_nb ? `${f.echelonnement_nb}×` : "Payer en plusieurs fois"}
                </button>
                {/* Mêmes boutons que dans Facturation : « Facture à jour » est la facture
                    recalculée à l'instant (rien n'est enregistré), et la copie envoyée dit
                    sa date. Avant, deux boutons « PDF » côte à côte ne disaient pas lequel
                    était celui reçu par la famille. */}
                <button className="btn-ghost" data-testid="eleve-facture-apercu"
                  title="La facture recalculée à l'instant, telle qu'elle partirait — rien n'est enregistré"
                  onClick={() => ouvrirPdf(
                    `/facturation/apercu-pdf?cle=${encodeURIComponent(f.cle)}&semestre=${f.semestre}`,
                    `Facture à jour — semestre ${f.semestre}`)}>
                  <Eye size={13} /> Facture à jour
                </button>
                {f.copie_numero && (
                  <button className="btn-ghost" data-testid="eleve-facture-pdf-envoye"
                    title={`Le PDF réellement reçu par la famille — ${f.copie_total} CHF`
                      + (Math.abs((f.copie_total || 0) - (f.total || 0)) > 0.02
                        ? ` — le calcul d'aujourd'hui donne ${f.total} CHF` : "")}
                    onClick={() => ouvrirPdf(
                      f.copie_source === "console"
                        ? `/facturation/pdf/${encodeURIComponent(f.copie_numero)}`
                        : f.copie_source === "version"
                          ? `/facturation/version/${encodeURIComponent(f.copie_numero)}/pdf`
                          : `/factures/archive/pdf?numero=${encodeURIComponent(f.copie_numero)}`,
                      `Facture envoyée ${f.numero || f.copie_numero}`)}>
                    <Eye size={13} />
                    {f.copie_date
                      ? `Copie envoyée le ${f.copie_date.split("-").reverse().join(".")}`
                      : "Copie envoyée à la famille"}
                  </button>
                )}
              </div>
            </Cellule>
          </>
        ))}
      />
      {modale?.quoi === "montants" && (
        <ModaleMontants facture={{ ...modale.facture, client: "cette famille" }} onFermer={() => setModale(null)}
          onOk={() => { setModale(null); charger(); onRecharger(); }} />
      )}
      {modale?.quoi === "echelon" && (
        <ModaleEchelonnement facture={{ ...modale.facture, client: "cette famille" }} onFermer={() => setModale(null)}
          onOk={() => { setModale(null); charger(); onRecharger(); }} />
      )}
    </Section>
  );
}

/* Une facture de stage sur la fiche : le même numéro, le même total et le même statut que
   dans Facturation › Stages. Deux gestes seulement : relire le PDF (celui reçu par la
   famille quand il est parti) et ouvrir la facture dans Facturation, où se font
   « Modifier » (prorata, rabais), « Envoyer », « Encaissé (banque) » et « Relancer ». */
function CellulesFactureStage({ f, famille }) {
  const ouvrirDansFacturation = () => window.dispatchEvent(new CustomEvent("console-aller", {
    detail: { onglet: "facturation", id: `stage::${f.cle}` } }));
  return (
    <>
      <Cellule fort>
        <span className="inline-flex items-center gap-1.5 flex-wrap">
          {f.stage || "Stage"} <Badge ton="violet">Stage</Badge>
        </span>
        <div className="text-xs font-normal text-[var(--muted)]">
          {f.dates}
          {(f.eleves || []).length > 0 && (
            <span className="block" data-testid="eleve-facture-stage-enfants">pour {f.eleves.join(", ")}</span>
          )}
        </div>
      </Cellule>
      <Cellule muet>{f.numero || "—"}
        {f.email && f.email.toLowerCase() !== famille && (
          <div className="text-xs mt-1">facturée à {f.email}</div>
        )}
      </Cellule>
      <Cellule droite fort>{f.total ? `${f.total} CHF` : "—"}</Cellule>
      <Cellule droite muet>{f.recu ? `${f.recu} CHF` : "—"}</Cellule>
      <Cellule>
        <BadgeStatut statut={f.statut} />
        {f.envoyee_le && (
          <div className="text-xs text-[var(--muted)] mt-1">
            envoyée le {jourFr(f.envoyee_le)}{f.echeance && f.solde > 0.02 ? ` · échéance ${jourFr(f.echeance)}` : ""}
          </div>
        )}
        {f.recu > 0 && f.solde > 0.02 && (
          <div className="text-xs mt-1" style={{ color: "#B45309" }} data-testid="eleve-facture-stage-solde">
            reste {f.solde} CHF
          </div>
        )}
        {f.recu > 0 && f.date_paiement && (
          <div className="text-xs text-[var(--muted)] mt-1">
            payé le {jourFr(f.date_paiement)}{f.mode_paiement ? ` · ${f.mode_paiement}` : ""}
          </div>
        )}
        {/* Prorata, rabais, ajustement : chacun sa ligne, libellée comme sur la facture. */}
        {(f.corrections || (f.ajustement ? [{ genre: "ajustement", montant: f.ajustement,
          libelle: `Ajustement${f.motif_ajustement ? ` — ${f.motif_ajustement}` : ""}` }] : []))
          .map((c) => (
            <div key={c.genre} className="text-xs text-[var(--muted)] mt-1" data-testid="eleve-facture-stage-correction">
              {c.libelle} · {c.montant > 0 ? "+" : ""}{c.montant} CHF
            </div>
          ))}
        {f.motif && <div className="text-xs text-[var(--muted)] mt-1">{f.motif}</div>}
      </Cellule>
      <Cellule>
        <div className="flex gap-1.5">
          {f.pdf_numero && (
            <button className="btn-ghost" data-testid="eleve-facture-stage-pdf"
              title={f.pdf_envoye_le ? "Le PDF reçu par la famille" : "La facture fabriquée, pas encore envoyée"}
              onClick={() => ouvrirPdf(`/stages/pdf/${encodeURIComponent(f.pdf_numero)}`,
                `Facture ${f.pdf_numero} — ${f.stage}`)}>
              <Eye size={13} />
              {f.pdf_envoye_le ? `Copie envoyée le ${jourFr(f.pdf_envoye_le)}` : "Voir la facture"}
            </button>
          )}
          <button className="btn-ghost" data-testid="eleve-facture-stage-facturation"
            title="Ouvre cette facture dans Facturation › Stages : modifier, envoyer, encaisser, relancer"
            onClick={ouvrirDansFacturation}>
            <Receipt size={13} /> Ouvrir dans Facturation
          </button>
        </div>
      </Cellule>
    </>
  );
}

/* Stages et auditions de l'élève : les mêmes menus déroulants que dans la Liste,
   pour ne pas avoir à ressortir la feuille du stage pour annuler un enfant. */
const STATUTS_ESSAI = ["Inscrit", "Annulé"];

/* Un cours d'essai se corrige par un bouton « Modifier », comme les inscriptions : on
   change la date (la famille décale sa venue) ou on annule. Deux statuts suffisent. */
function BlocEssais({ essais, cours, onRecharger }) {
  const { informer } = useConfirmation();
  const [edite, setEdite] = useState(null);
  const [occupe, setOccupe] = useState(false);

  const enregistrer = async () => {
    setOccupe(true);
    try {
      await api(`/essais/${encodeURIComponent(edite.id)}`, {
        method: "PATCH",
        body: JSON.stringify({ champs: {
          date_essai: edite.date_essai || "",
          statut: edite.statut || "Inscrit",
          cours_id: edite.cours_id,
        } }),
      });
      setEdite(null);
      onRecharger?.();
    } catch (e) {
      await informer({ titre: "Modification impossible", ton: "danger", texte: e.message });
    }
    setOccupe(false);
  };

  return (
    <Section titre={<span>Cours d'essai <span className="text-sm text-[var(--muted)]">({essais.length})</span></span>}
      aide="La date et le cours se corrigent avec « Modifier » : la famille décale sa venue, ou annule. Un cours d'essai n'entre jamais dans la facture du semestre : il est gratuit pendant les portes ouvertes, sinon il se paie sur place (prix des Réglages)."
      testid="eleve-essais-section">
      <Tableau
        testid="eleve-essais"
        entetes={["Cours", "Jour", "Date", "Statut", "Prix CHF", ""]}
        lignes={essais}
        vide="Aucun cours d'essai."
        rendu={(l) => (
          <>
            <Cellule fort>{l.cours_nom || "—"}</Cellule>
            <Cellule muet>{l.jour || "—"} {l.heure || ""}</Cellule>
            <Cellule>{(l.date_essai || "").split("-").reverse().join(".") || "—"}</Cellule>
            <Cellule>
              <Badge ton={String(l.statut || "").startsWith("Annul") ? "bleu" : "vert"}>
                {l.statut || "Inscrit"}
              </Badge>
            </Cellule>
            {Number(l.essai_payant) > 0 ? (
              <Cellule fort>
                {Number.isInteger(Number(l.essai_payant)) ? `${Number(l.essai_payant)}.–` : Number(l.essai_payant).toFixed(2)} sur place
              </Cellule>
            ) : (
              <Cellule muet>{l.essai_payant === 0 ? "gratuit" : "—"}</Cellule>
            )}
            <Cellule droite>
              <button className="btn-ghost" data-testid="essai-modifier"
                onClick={() => setEdite({ ...l, statut: l.statut || "Inscrit" })}>
                <Pencil size={13} /> Modifier
              </button>
            </Cellule>
          </>
        )}
      />

      {edite && (
        <Modale titre={`Cours d'essai — ${edite.cours_nom || ""}`} onFermer={() => setEdite(null)}
          testid="modale-essai">
          <div className="space-y-3">
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Cours</span>
              <select className="input-field w-full mt-1" data-testid="essai-cours"
                value={edite.cours_id || ""}
                onChange={(e) => setEdite({ ...edite, cours_id: e.target.value })}>
                <option value={edite.cours_id || ""}>{edite.cours_nom || "—"}</option>
                {cours.filter((c) => c.id !== edite.cours_id)
                  .map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Date de l'essai</span>
              <input type="date" className="input-field w-full mt-1" data-testid="essai-date"
                value={(edite.date_essai || "").slice(0, 10)}
                onChange={(e) => setEdite({ ...edite, date_essai: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Statut</span>
              <select className="input-field w-full mt-1" data-testid="essai-statut"
                value={edite.statut}
                onChange={(e) => setEdite({ ...edite, statut: e.target.value })}>
                {STATUTS_ESSAI.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <p className="text-[11px] text-[var(--muted)]">
              « Annulé » sort l'essai des cours à venir et rend la place au cours. Rien n'est
              effacé : la ligne reste sur la fiche.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button className="btn-ghost" onClick={() => setEdite(null)}>Annuler</button>
              <BoutonSombre onClick={enregistrer} disabled={occupe} testid="essai-enregistrer">
                {occupe ? <Loader2 size={14} className="animate-spin" /> : null} Enregistrer
              </BoutonSombre>
            </div>
          </div>
        </Modale>
      )}
    </Section>
  );
}

/* Le paiement d'un workshop sur la fiche : le bouton « Encaisser » de Facturation › Cash /
   espèces, au lieu d'un menu « Payé » (20.10.2026). Montant dû rabais déduit, ce qui est
   reçu et ce qui reste, calculés par le serveur (workshops.a_payer / recu). */
function PaiementWorkshop({ l, onFait }) {
  const [ouvert, setOuvert] = useState(false);
  const du = Number(l.a_payer ?? l.montant) || 0;
  const recu = Number(l.recu) || 0;
  const reste = Math.max(Math.round((du - recu) * 100) / 100, 0);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="eleve-workshop-paiement">
      <span className="text-sm font-semibold whitespace-nowrap">{argent(du)}</span>
      {/^(annul|doublon)/i.test(String(l.statut || "").trim()) ? (
        <span className="text-xs text-[var(--muted)]">annulé — rien à encaisser</span>
      ) : reste <= 0.02 ? (
        <span className="inline-flex items-center gap-1 rounded-full text-[12px] font-semibold px-2.5 py-1"
          style={{ background: "#F0FDF4", color: "#15803D", border: "1px solid #BBF7D0" }}
          data-testid="eleve-workshop-encaisse">
          ✓ Encaissé{l.paye_le ? ` le ${String(l.paye_le).slice(0, 10).split("-").reverse().join(".")}` : ""}
        </span>
      ) : (
        <>
          <button className="btn-primary" data-testid="eleve-workshop-encaisser" onClick={() => setOuvert(true)}
            style={{ padding: "6px 12px", fontSize: 12, gap: 5, boxShadow: "none" }}>
            <Banknote size={12} /> Encaisser
          </button>
          {recu > 0.004 && (
            <span className="text-xs text-[var(--muted)]">{argent(recu)} reçus · reste {argent(reste)}</span>
          )}
        </>
      )}
      {ouvert && (
        <ModaleEncaisser onFermer={() => setOuvert(false)}
          onOk={async () => { setOuvert(false); await onFait?.(); }}
          it={{ ref: `workshop::${l.id}`, genre: "workshop", titre: `${l.prenom || ""} ${l.nom || ""}`.trim(),
            quoi: l.seances_txt || "Workshops heels", total: du, recu, reste,
            echelonnement: l.echelonnement_nb ? { nb: l.echelonnement_nb, part: Math.round((du / l.echelonnement_nb) * 100) / 100 } : null }} />
      )}
    </div>
  );
}

function BlocParticipations({ titre, aide, famille, lignes, colonnes, selects, paiement, testid }) {
  const [etat, setEtat] = useState(lignes);
  const [erreur, setErreur] = useState("");
  useEffect(() => setEtat(lignes), [lignes]);

  const modifier = async (l, colonne, valeur) => {
    setEtat((ls) => ls.map((x) => (x.id === l.id ? { ...x, [colonne]: valeur } : x)));
    setErreur("");
    try {
      await api(`/participations/${famille}/${encodeURIComponent(l.id)}`, {
        method: "PATCH", body: JSON.stringify({ champs: { [colonne]: valeur } }),
      });
    } catch (e) {
      setErreur(e.message);
    }
  };

  return (
    <Section titre={<span>{titre} <span className="text-sm text-[var(--muted)]">({etat.length})</span></span>}
      aide={aide} testid={`${testid}-section`}>
      {erreur && <p className="text-sm" style={{ color: "#B91C1C" }} data-testid={`${testid}-erreur`}>{erreur}</p>}
      <Tableau
        testid={testid}
        entetes={[...colonnes.map(([, label]) => label), ...(paiement ? [paiement.label] : []),
          ...selects.map(([, label]) => label)]}
        lignes={etat}
        vide="Rien pour l'instant."
        rendu={(l) => (
          <>
            {colonnes.map(([c], i) => (
              <Cellule key={c} fort={i === 0} muet={i > 0}>{l[c] === 0 ? 0 : l[c] || "—"}</Cellule>
            ))}
            {paiement && <Cellule largeur={220}>{paiement.rendu(l)}</Cellule>}
            {selects.map(([c, , opts]) => (
              <Cellule key={c}>
                <SelectLigne valeur={l[c]} options={opts} testid={`${testid}-${c}`}
                  onChange={(v) => modifier(l, c, v)} />
              </Cellule>
            ))}
          </>
        )}
      />
    </Section>
  );
}

const Champ = ({ label, valeur, alerte, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">{label}</div>
    <div className="mt-0.5" style={{ color: alerte ? "#B45309" : "var(--text)" }}>{valeur || "—"}</div>
  </div>
);

function useEnvoi() {
  const [etat, setEtat] = useState({ envoi: false, err: "" });
  const lancer = async (fn) => {
    setEtat({ envoi: true, err: "" });
    try { await fn(); return true; } catch (e) { setEtat({ envoi: false, err: e.message }); return false; }
  };
  return [etat, lancer];
}

/* Archiver une fiche est rare (fiche en double ou créée par erreur). Le grand cadre rouge
   toujours visible inquiétait l'école (30.10.2026) : plié par défaut, un petit lien gris ;
   déplié, une explication calme et le vrai bouton (qui demande encore confirmation). */
function ArchiverLaFiche({ onArchiver }) {
  const [ouvert, setOuvert] = useState(false);
  if (!ouvert) {
    return (
      <div className="mb-4 mt-1">
        <button type="button" data-testid="eleve-archiver-deplier" onClick={() => setOuvert(true)}
          className="inline-flex items-center gap-1 text-xs text-[var(--muted)] underline underline-offset-2 hover:text-[var(--text)]">
          <Archive size={12} /> Archiver la fiche…
        </button>
      </div>
    );
  }
  return (
    <div className="rounded-xl p-4 mb-4 mt-1" data-testid="fiche-archiver"
      style={{ background: "var(--bg-soft)", border: "1px solid var(--border)" }}>
      <p className="text-sm text-[var(--muted)]">
        Seulement pour une fiche en double ou créée par erreur. Elle est rangée de côté :
        rien n'est effacé, et elle revient d'un clic depuis le filtre « Archivés ».
      </p>
      <div className="flex flex-wrap gap-2 mt-2">
        <button type="button" className="btn-ghost" data-testid="eleve-archiver" onClick={onArchiver}>
          <Archive size={13} /> Archiver cette fiche
        </button>
        <button type="button" className="btn-ghost" data-testid="eleve-archiver-replier"
          onClick={() => setOuvert(false)}>
          Ne pas archiver
        </button>
      </div>
    </div>
  );
}

function FormulaireFiche({ eleve, famille, onFermer, onOk, onArchiver }) {
  const [el, setEl] = useState({ ...eleve });
  const [fa, setFa] = useState({ ...famille });
  const [etat, lancer] = useEnvoi();

  const enregistrer = () => lancer(async () => {
    await api(`/eleves/${encodeURIComponent(eleve.id)}`, {
      method: "PATCH",
      body: JSON.stringify({ champs: {
        prenom: el.prenom, nom: el.nom, date_naissance: el.date_naissance, avs: el.avs,
        droit_image: el.droit_image || "",
      } }),
    });
    await api(`/familles/${encodeURIComponent(famille.id)}`, {
      method: "PATCH",
      body: JSON.stringify({ champs: {
        prenom_responsable: fa.prenom_responsable, nom_responsable: fa.nom_responsable,
        email: fa.email, telephone: fa.telephone, adresse: fa.adresse, npa: fa.npa, localite: fa.localite,
      } }),
    });
    onOk();
  });

  return (
    <Modale large titre="Modifier la fiche" onFermer={onFermer} testid="modale-eleve"
      sous_titre="L'élève et son responsable, au même endroit.">
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--muted)] mb-3">L'élève</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <ChampTexte label="Prénom" valeur={el.prenom} onChange={(v) => setEl({ ...el, prenom: v })} testid="champ-prenom" />
        <ChampTexte label="Nom" valeur={el.nom} onChange={(v) => setEl({ ...el, nom: v })} testid="champ-nom" />
        <ChampTexte label="Date de naissance" type="date" valeur={el.date_naissance} onChange={(v) => setEl({ ...el, date_naissance: v })}
          testid="champ-naissance" aide={age(el.date_naissance) !== "" ? `${age(el.date_naissance)} ans · ${estAdulte(el.date_naissance) ? "adulte" : "enfant"}` : ""} />
        <ChampTexte label="N° AVS" valeur={el.avs} onChange={(v) => setEl({ ...el, avs: v })} testid="champ-avs"
          aide="Nécessaire pour l'attestation tarif 595" />
        <ChampChoix label="Droit à l'image" valeur={el.droit_image || ""} options={DROITS_IMAGE}
          onChange={(v) => setEl({ ...el, droit_image: v })} testid="champ-droit-image" />
      </div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--muted)] mb-3 mt-2">Le responsable</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <ChampTexte label="Prénom" valeur={fa?.prenom_responsable} onChange={(v) => setFa({ ...fa, prenom_responsable: v })} testid="champ-resp-prenom" />
        <ChampTexte label="Nom" valeur={fa?.nom_responsable} onChange={(v) => setFa({ ...fa, nom_responsable: v })} testid="champ-resp-nom" />
        <ChampTexte label="E-mail" valeur={fa?.email} onChange={(v) => setFa({ ...fa, email: v })} testid="champ-email" />
        <ChampTexte label="Téléphone" valeur={fa?.telephone} onChange={(v) => setFa({ ...fa, telephone: v })} testid="champ-telephone" />
        <ChampTexte label="Adresse" valeur={fa?.adresse} onChange={(v) => setFa({ ...fa, adresse: v })} testid="champ-adresse" />
        <div className="grid grid-cols-2 gap-3">
          <ChampTexte label="NPA" valeur={fa?.npa} onChange={(v) => setFa({ ...fa, npa: v })} testid="champ-npa" />
          <ChampTexte label="Localité" valeur={fa?.localite} onChange={(v) => setFa({ ...fa, localite: v })} testid="champ-localite" />
        </div>
      </div>
      {etat.err && <p className="text-sm mb-3" style={{ color: "#B91C1C" }}>{etat.err}</p>}
      {onArchiver && <ArchiverLaFiche onArchiver={onArchiver} />}
      <div className="flex gap-2">
        <button className="btn-ghost" onClick={onFermer}>Annuler</button>
        <BoutonSombre onClick={enregistrer} disabled={etat.envoi} testid="modale-eleve-enregistrer">Enregistrer</BoutonSombre>
      </div>
    </Modale>
  );
}

const CHANGE_DE_COURS = "Changé de cours";
const MOTIF_TRANSFERT = "Transfert vers un autre cours (audition, réorientation)";

/* « Vers quel cours ? » — UN seul choix, depuis le bouton « Changer de cours » comme depuis
   le statut « Changé de cours » (école, 09.10.2026 : « comment simplifier ? »).
   · En tête, les cours que l'élève SUIT DÉJÀ ce semestre : la ligne devient « Changé de
     cours → … » (plus facturée), sans créer de doublon.
   · Ensuite tous les autres cours : la ligne passe dans le nouveau cours (même date
     d'inscription, pas de nouveau prorata, prix du nouveau cours), l'ancien reste dans
     l'historique. */
const DEJA_SUIVI = "deja:";
/* « Mardi 17:00 — Hip-hop 9-11 ans (8/12) » : un cours du planning, le même libellé dans
   tous les choix de cours de la fiche. */
const libelleCours = (c) => `${c.jour} ${c.debut} — ${c.nom} (${c.nb_inscrits}/${c.places_max || "?"})`;
function useChoixNouveauCours(inscription, inscriptions) {
  const [cours, setCours] = useState([]);
  useEffect(() => { api("/liste/cours").then((d) => setCours(d.items || [])).catch(() => {}); }, []);
  const suivis = (inscriptions || []).filter((a) => a.id !== inscription.id
    && a.cours_id !== inscription.cours_id && !estCarte10(a) && encoreActive(a)
    && String(a.semestre || "") === String(inscription.semestre || ""));
  const options = [{ valeur: "", label: "Choisir un cours…" },
    ...suivis.map((a) => ({ valeur: DEJA_SUIVI + a.id,
      label: `Déjà suivi : ${a.jour || ""} ${a.heure || ""} — ${a.cours_nom}` })),
    ...cours.filter((c) => c.id !== inscription.cours_id && !suivis.some((a) => a.cours_id === c.id))
      .map((c) => ({ valeur: c.id, label: libelleCours(c) }))];
  return { cours, suivis, options };
}

async function appliquerChangementDeCours(inscription, choix, suivis, depuisLeDebut = false) {
  if (choix.startsWith(DEJA_SUIVI)) {
    const vers = suivis.find((a) => DEJA_SUIVI + a.id === choix);
    await api(`/inscriptions/${encodeURIComponent(inscription.id)}`, {
      method: "PATCH", body: JSON.stringify({ champs: {
        statut: "Annulé", motif_desinscription: MOTIF_TRANSFERT, changement_de_cours: true,
        change_vers: vers?.cours_nom || "" } }),
    });
    return { message: `« ${inscription.cours_nom} » → « ${vers?.cours_nom || ""} » : changement noté, `
      + "cette ligne n'est plus facturée." };
  }
  return api(`/inscriptions/${encodeURIComponent(inscription.id)}/changer-cours`, {
    method: "POST", body: JSON.stringify({ cours_id: choix, depuis_le_debut: !!depuisLeDebut }),
  });
}

/* Ce que fera le choix, en une phrase, sous la liste. */
function ExplicationChangement({ choix, cours }) {
  if (!choix) return null;
  if (choix.startsWith(DEJA_SUIVI)) {
    return (
      <p className="text-xs mb-4" style={{ color: "#0F766E" }} data-testid="changement-explication">
        L'élève suit déjà ce cours : cette ligne passe en « Changé de cours → … » et n'est plus
        facturée. Aucune nouvelle ligne n'est créée. Si la facture est déjà partie, elle passe
        dans « À corriger ».
      </p>
    );
  }
  const cible = cours.find((c) => c.id === choix);
  return (
    <>
      {cible && (
        <p className="text-xs mb-2" style={{ color: cible.nb_inscrits >= cible.places_max ? "#B45309" : "#15803D" }}>
          {cible.nb_inscrits >= cible.places_max
            ? "Ce cours est complet : à toi de décider si tu forces le passage."
            : `${(cible.places_max || 0) - cible.nb_inscrits} place(s) libre(s).`}
        </p>
      )}
      <p className="text-xs text-[var(--muted)] mb-4" data-testid="changement-explication">
        La ligne passe dans ce cours (même date d'inscription, pas de nouveau prorata) au prix
        du nouveau cours (forfait, sinon barème de la durée selon l'âge). L'ancien cours reste
        dans l'historique. Si la facture est déjà partie, elle passe dans « À corriger ».
      </p>
    </>
  );
}

/* « Vers lequel de ses cours ? » pour une ligne déjà quittée. En tête, les AUTRES cours que
   l'élève suit (un choix par cours, ceux du même semestre d'abord) : le choix ne fait
   qu'afficher « Changé de cours → … » sur la ligne. Ensuite TOUS les autres cours actifs du
   planning, avec le même libellé que « Changer de cours » : l'école ne voyait que « Aucun
   autre cours sur la fiche » et demandait où était passée la liste (09.10.2026). Choisir un
   cours qu'il ne suit pas encore l'y inscrit dans le même enregistrement (VERS_COURS). */
const VERS_COURS = "cours:";
const pasQuittee = (a) => !ligneQuittee(a);
function optionsChangeVers(inscription, inscriptions, cours) {
  const memeSemestre = (a) => String(a.semestre || "") === String(inscription.semestre || "");
  const vus = new Set();
  const autres = (inscriptions || [])
    .filter((a) => a.id !== inscription.id && a.cours_id !== inscription.cours_id && pasQuittee(a))
    .sort((a, b) => Number(memeSemestre(b)) - Number(memeSemestre(a)))
    .filter((a) => !vus.has(a.cours_nom) && vus.add(a.cours_nom));
  // Un cours déjà sur la fiche (inscrit ou en liste d'attente) n'est pas proposé deux fois.
  const suivis = new Set((inscriptions || []).filter(pasQuittee).map((a) => a.cours_id));
  const planning = (cours || []).filter((c) => c.id !== inscription.cours_id && !suivis.has(c.id));
  const options = [{ valeur: "", label: "Pas précisé" },
    ...autres.map((a) => ({ valeur: a.cours_nom,
      label: `${encoreActive(a) ? "Déjà suivi" : "En liste d'attente"} : ${[a.jour, a.heure].filter(Boolean).join(" ")} — ${a.cours_nom}${memeSemestre(a) ? "" : ` · ${a.semestre}`}` })),
    ...planning.map((c) => ({ valeur: VERS_COURS + c.id, label: libelleCours(c) }))];
  // Un cours noté avant (et quitté depuis) reste proposé : enregistrer ne l'efface pas.
  if (inscription.change_vers && !vus.has(inscription.change_vers)) {
    options.splice(1, 0, { valeur: inscription.change_vers, label: inscription.change_vers });
  }
  return { options, nb: autres.length };
}

/* Ce que fait le choix de « Vers lequel de ses cours ? », AVANT d'enregistrer. */
function ExplicationChangeVers({ inscription, vers, cours }) {
  if (vers.startsWith(VERS_COURS)) {
    const cible = cours.find((c) => VERS_COURS + c.id === vers);
    const complet = !!cible && cible.places_max > 0 && cible.nb_inscrits >= cible.places_max;
    return (
      <div className="rounded-xl p-3 mb-4 text-xs space-y-1" style={{ background: "#F0FDFA", border: "1px solid #99F6E4", color: "#0F766E" }}
        data-testid="changement-explication">
        <p className="font-semibold">L'élève ne suit pas encore « {cible?.nom || "ce cours"} » : en enregistrant, il y est inscrit.</p>
        <p>
          {complet
            ? "Ce cours est complet : l'inscription part en liste d'attente, rien n'est facturé tant qu'une place ne se libère pas."
            : `Statut « Inscrit »${cible ? ` (${(cible.places_max || 0) - cible.nb_inscrits} place(s) libre(s))` : ""}, au prix du nouveau cours (forfait, sinon barème de la durée selon l'âge), sur la même facture.`}
          {" "}Pas de prorata d'arrivée : c'est un changement de cours, l'élève était déjà là.
        </p>
        <p>
          Cette ligne reste « {inscription.statut} » et s'affiche « Changé de cours → {cible?.nom || "…"} ».
          Si la facture est déjà partie, elle passe dans « À corriger ».
        </p>
      </div>
    );
  }
  return (
    <p className="text-xs mb-4" style={{ color: "#0F766E" }} data-testid="changement-explication">
      La ligne reste « {inscription.statut} » en coulisses : elle n'est ni déplacée ni refacturée,
      aucune facture déjà envoyée ne change. Elle s'affiche « Changé de cours{vers ? ` → ${vers}` : ""} »
      sur la fiche. Seule conséquence, sur une facture pas encore envoyée : le cours qui la
      remplace ce semestre ne compte pas de prorata d'arrivée (l'élève était déjà là).
    </p>
  );
}

function FormulaireInscription({ inscription, inscriptions, onFermer, onOk }) {
  const statutInitial = changeDeCours(inscription) ? CHANGE_DE_COURS : (inscription.statut || "Inscrit");
  const { cours, suivis, options: optionsCours } = useChoixNouveauCours(inscription, inscriptions);
  const [nouveauCours, setNouveauCours] = useState("");
  // Ligne déjà annulée ou désinscrite : « Changé de cours » la MARQUE comme un changement
  // (son statut réel ne bouge pas) au lieu de la déplacer. Avant, elle partait dans le cours
  // choisi en restant « Annulé » : « ça reste sur annulé » (école, 09.10.2026).
  const quittee = ligneQuittee(inscription);
  const [vers, setVers] = useState(inscription.change_vers || "");
  const choixVers = optionsChangeVers(inscription, inscriptions, cours);
  // Une carte 10 cours n'est jamais un changement de cours (facturation, 09.10.2026) : une
  // carte annulée ne propose pas ce statut, sa ligne ne l'afficherait de toute façon pas.
  const statutsProposes = quittee && estCarte10(inscription) && statutInitial !== CHANGE_DE_COURS
    ? STATUTS : [...STATUTS.slice(0, 2), CHANGE_DE_COURS, ...STATUTS.slice(2)];
  const [f, setF] = useState({
    statut: statutInitial,
    desinscription_le: inscription.desinscription_le || "",
    motif_desinscription: inscription.motif_desinscription || "",
    notes_internes: inscription.notes_internes || "",
    place_conservee: inscription.place_conservee || "",
    formule: inscription.formule || "",
  });
  const [impact, setImpact] = useState(null);
  // Carte 10 cours : comment elle se paie — au comptoir (cash / TWINT) ou sur la facture du
  // semestre (QR). Passer une ligne à la carte oblige à le dire (école, 30.10.2026).
  const modeActuel = estCarte10(inscription) ? (inscription.carte10_sur_facture ? "facture" : "cash") : "";
  const [modeCarte, setModeCarte] = useState(modeActuel);
  const carteChoisie = estCarte10(f);
  const modeCarteManquant = carteChoisie && !modeCarte;
  // Cours sans prix (À traiter › « Cours sans prix — à corriger ») : le montant écrit ici
  // devient le prix de la ligne, et la facture de la famille se débloque.
  const [prix, setPrix] = useState("");
  const prixEcrit = inscription.prix_inconnu && Number(prix) > 0 ? { montant_annonce: Number(prix) } : {};
  const [etat, lancer] = useEnvoi();
  // Passer une ligne EN COURS à « Changé de cours » : on dit vers quel cours, exactement
  // comme avec le bouton « Changer de cours » (même geste, même résultat).
  const versChangement = f.statut === CHANGE_DE_COURS && statutInitial !== CHANGE_DE_COURS && !quittee;
  // Ligne déjà quittée : « Changé de cours » ne fait que la marquer (cf. `quittee`).
  const marqueChangement = f.statut === CHANGE_DE_COURS && quittee;
  // « Changé de cours » s'enregistre comme une inscription annulée (pas facturée) qui
  // porte le drapeau et le motif « Transfert… » : le nouveau cours reprend sa date
  // d'entrée (pas de prorata) et la fiche affiche « Changé de cours → nouveau cours ».
  // Une ligne déjà annulée ou désinscrite garde son statut : rien ne change pour la facture.
  const statutReel = f.statut === CHANGE_DE_COURS ? (quittee ? inscription.statut : "Annulé") : f.statut;
  // L'aperçu « Ce que ce changement fait » ne parle que d'un VRAI changement de statut.
  const change = statutReel !== (inscription.statut || "Inscrit");

  useEffect(() => {
    if (!change || versChangement) return setImpact(null);
    const p = new URLSearchParams({ statut: statutReel, date_annonce: f.desinscription_le || "" });
    api(`/inscriptions/${encodeURIComponent(inscription.id)}/impact?${p}`).then(setImpact).catch(() => setImpact(null));
  }, [f.statut, statutReel, f.desinscription_le, inscription, change, versChangement]);

  const choixPlaceManquant = !!impact?.choix_place && !f.place_conservee;

  const enregistrer = () => lancer(async () => {
    if (versChangement) {
      // La même route que le bouton « Changer de cours » ; son message (nouveau cours, prix,
      // facture « À corriger ») s'affiche aussi d'ici : rien ne s'affichait après
      // l'enregistrement (école, 09.10.2026). Un refus reste affiché dans la fenêtre.
      const r = await appliquerChangementDeCours(inscription, nouveauCours, suivis);
      return onOk(r?.message);
    }
    const { statut, ...reste } = f;
    if (marqueChangement && vers.startsWith(VERS_COURS)) {
      // Un cours du planning que l'élève ne suit pas encore : le serveur l'y inscrit et
      // marque cette ligne « Changé de cours → … », en un seul enregistrement.
      const r = await api(`/inscriptions/${encodeURIComponent(inscription.id)}`, { method: "PATCH",
        body: JSON.stringify({ champs: { ...reste, changement_de_cours: true, motif_desinscription: MOTIF_TRANSFERT,
          vers_cours_id: vers.slice(VERS_COURS.length) } }) });
      return onOk(r.message);
    }
    const champs = marqueChangement
      // Pas de « statut » envoyé : la ligne reste « Annulé » / « Désinscrit », sans nouvel
      // effet sur la facture ni sur la place. Seuls le drapeau, le motif et le cours suivi.
      ? { ...reste, changement_de_cours: true, motif_desinscription: MOTIF_TRANSFERT, change_vers: vers }
      : { ...f, ...(changeDeCours(inscription) || inscription.changement_de_cours ? { changement_de_cours: false } : {}),
          // « Annulé » tout court n'est plus un transfert : le motif « Transfert… » s'en va.
          ...(/^transfert/i.test(String(f.motif_desinscription || "")) ? { motif_desinscription: "" } : {}),
          ...prixEcrit,
          ...(carteChoisie && modeCarte && (modeCarte !== modeActuel || !estCarte10(inscription))
            ? { carte10_mode: modeCarte } : {}) };
    await api(`/inscriptions/${encodeURIComponent(inscription.id)}`, { method: "PATCH", body: JSON.stringify({ champs }) });
    onOk();
  });

  return (
    <Modale large titre={inscription.cours_nom} sous_titre={`${inscription.jour} ${inscription.heure} · ${inscription.semestre}`}
      onFermer={onFermer} testid="modale-inscription">
      <ChampChoix label="Statut" valeur={f.statut} options={statutsProposes}
        onChange={(v) => setF({ ...f, statut: v })} testid="champ-statut"
        aide={quittee
          ? "Ligne déjà quittée : « Changé de cours » la note comme un changement de cours (l'élève n'a pas quitté l'école), sans la déplacer — puis « Vers lequel » : un de ses cours, ou un cours du planning qu'il ne suit pas encore (il y est inscrit) · Annulé avant le début du semestre · Désinscrit en cours de semestre · Inscrit ou Liste d'attente la remettent en cours."
          : "Inscrit · Liste d'attente quand le cours est plein · Changé de cours quand l'élève quitte ce cours POUR un autre (audition réussie, cours qui ferme) : tu choisis ensuite le cours · Annulé avant le début du semestre · Désinscrit en cours de semestre."} />
      {versChangement && (
        <>
          <ChampChoix label="Vers quel cours ?" valeur={nouveauCours} options={optionsCours}
            onChange={setNouveauCours} testid="champ-nouveau-cours" />
          <ExplicationChangement choix={nouveauCours} cours={cours} />
        </>
      )}
      {marqueChangement && (
        <>
          <ChampChoix label="Vers lequel de ses cours ? (facultatif)" valeur={vers} options={choixVers.options}
            onChange={setVers} testid="champ-change-vers"
            aide={choixVers.nb
              ? "D'abord les autres cours que l'élève suit, puis tous les cours du planning : choisir un cours qu'il ne suit pas encore l'y inscrit."
              : "Son nouveau cours n'est pas encore sur la fiche : choisis-le dans le planning, il y sera inscrit en enregistrant."} />
          <ExplicationChangeVers inscription={inscription} vers={vers} cours={cours} />
        </>
      )}
      {f.statut === "Désinscrit" && (
        <ChampTexte label="Désinscription annoncée le" type="date" valeur={String(f.desinscription_le || "").slice(0, 10)}
          onChange={(v) => setF({ ...f, desinscription_le: v })} testid="champ-date-annonce"
          aide="C'est cette date qui décide du délai (règlement art. 1 : deux mois avant le semestre suivant)" />
      )}
      {["Désinscrit", "Annulé"].includes(f.statut) && (
        <ChampChoix label="Motif" valeur={/^transfert/i.test(f.motif_desinscription || "") ? "" : f.motif_desinscription}
          options={MOTIFS.filter((m) => !/^transfert/i.test(m))}
          onChange={(v) => setF({ ...f, motif_desinscription: v })} testid="champ-motif"
          aide="L'élève quitte ce cours pour un autre ? Choisis plutôt le statut « Changé de cours »." />
      )}
      {inscription.prix_inconnu && (
        <div className="rounded-xl p-4 mb-4" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}
          data-testid="prix-inconnu">
          <div className="font-semibold text-sm mb-1" style={{ color: "#991B1B" }}>Cours sans prix</div>
          <p className="text-xs mb-3" style={{ color: "#991B1B" }}>
            La console ne sait pas calculer le prix de ce cours
            {inscription.prix_inconnu_raison ? ` : ${inscription.prix_inconnu_raison}` : ""}. La facture
            de la famille est bloquée. Écris ici le montant à facturer pour ce cours — ou ajoute le
            tarif dans Réglages › Tarifs et forfaits : la ligne prendra alors le prix toute seule.
          </p>
          <ChampTexte label="Prix de ce cours (CHF)" type="number" valeur={prix} onChange={setPrix}
            testid="champ-prix-cours" />
        </div>
      )}
      <ChampChoix label="Formule" valeur={f.formule} testid="champ-formule"
        options={[...new Set([...FORMULES, inscription.formule || ""])]}
        onChange={(v) => setF({ ...f, formule: v })}
        aide="« Carte 10 cours » : 250 CHF pour 10 séances, valable jusqu'à la fin du semestre, payée au comptoir (Facturation › Cash / espèces) ou sur la facture du semestre. Elle n'ouvre ni la réduction d'accumulation ni la réduction fratrie. Un nouvel élève qui n'a que des cartes paie la taxe d'inscription avec sa première carte (270) ; s'il suit aussi un cours au semestre, la taxe reste sur sa facture." />
      {carteChoisie && (
        <ChampChoix label="La carte se paie" valeur={modeCarte} testid="champ-carte10-mode"
          options={[{ valeur: "", label: "— choisir —" }, { valeur: "cash", label: "Au comptoir (cash / TWINT)" },
            { valeur: "facture", label: "Sur la facture du semestre (QR)" }]}
          onChange={setModeCarte}
          aide="Sur la facture : la carte (250 CHF) devient une ligne de la facture de la famille ; une facture déjà envoyée passe dans « À corriger » pour être refaite." />
      )}
      <ChampTexte label="Notes internes" valeur={f.notes_internes} onChange={(v) => setF({ ...f, notes_internes: v })} testid="champ-notes" />

      {impact?.choix_place && (
        <div className="rounded-xl p-4 mb-4" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}
          data-testid="choix-place">
          <div className="font-semibold text-sm mb-1" style={{ color: "#92400E" }}>
            Hors délai : que fait-on de sa place ?
          </div>
          <p className="text-xs mb-2" style={{ color: "#92400E" }}>
            Le semestre suivant est dû et sera facturé. Tu peux malgré tout remettre la place
            en vente si l'élève ne vient plus.
          </p>
          <ChampChoix label="Place dans le quota du cours" valeur={f.place_conservee} testid="champ-place"
            options={[{ valeur: "", label: "À choisir…" },
              { valeur: "Oui", label: "Garder la place réservée pour cet élève" },
              { valeur: "Non", label: "Libérer la place — le cours peut la revendre" }]}
            onChange={(v) => setF({ ...f, place_conservee: v })} />
        </div>
      )}

      {impact && change && (
        <div className="rounded-xl p-4 mb-4 text-sm" style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }} data-testid="impact-facturation">
          <div className="font-semibold mb-1">
            Ce que ce changement fait — {inscription.statut || "Inscrit"} → {f.statut}
          </div>
          <ul className="mt-1 space-y-1 text-xs text-[var(--muted)]">
            {(impact.explications || [impact.motif]).map((t, k) => <li key={k}>· {t}</li>)}
          </ul>
        </div>
      )}
      {/* Un refus du serveur (cours inconnu, ligne déjà dans ce cours…) se lit ici, juste
          au-dessus d'« Enregistrer » : la fenêtre reste ouverte. */}
      {etat.err && (
        <p className="text-sm mb-3 rounded-xl px-3 py-2" role="alert" data-testid="modale-inscription-erreur"
          style={{ color: "#B91C1C", background: "#FEF2F2", border: "1px solid #FECACA" }}>{etat.err}</p>
      )}
      <div className="flex gap-2">
        <button className="btn-ghost" onClick={onFermer}>Annuler</button>
        <BoutonSombre onClick={enregistrer} disabled={etat.envoi || choixPlaceManquant || modeCarteManquant || (versChangement && !nouveauCours)}
          testid="modale-inscription-enregistrer">
          Enregistrer
        </BoutonSombre>
        {choixPlaceManquant && (
          <span className="text-xs self-center" style={{ color: "#B45309" }}>
            Choisis d'abord ce que devient sa place.
          </span>
        )}
        {modeCarteManquant && (
          <span className="text-xs self-center" style={{ color: "#B45309" }}>
            Dis comment la carte se paie.
          </span>
        )}
      </div>
    </Modale>
  );
}

/* Changer de cours : le nouveau cours est facturé à SON prix (forfait ou barème de la
   durée). Plus d'ajustement à la main — la facture repasse par « À corriger » où l'école
   valide le nouveau total. */
function FormulaireChangementCours({ inscription, inscriptions, eleve, onFermer, onOk }) {
  const { cours, suivis, options } = useChoixNouveauCours(inscription, inscriptions);
  const [choix, setChoix] = useState("");
  // « Depuis quand ? » : un vrai changement (à partir d'aujourd'hui), ou une erreur
  // d'inscription corrigée depuis le début (Jen, 02.11.2026 : élève inscrit dans le mauvais
  // cours du samedi, la paie du prof d'octobre était déjà validée).
  const [depuis, setDepuis] = useState("aujourdhui");
  const [resultat, setResultat] = useState(null);
  const [etat, lancer] = useEnvoi();
  const versUnCours = !!choix && !choix.startsWith(DEJA_SUIVI);

  const changer = () => lancer(async () => {
    const r = await appliquerChangementDeCours(inscription, choix, suivis, versUnCours && depuis === "debut");
    setResultat(r);
    onOk(r.message);
  });

  return (
    <Modale large titre="Changer de cours"
      sous_titre={`${eleve.prenom} ${eleve.nom} — actuellement « ${inscription.cours_nom} » à ${inscription.montant_annonce || 0} CHF`}
      onFermer={onFermer} testid="modale-changement-cours">
      <ChampChoix label="Vers quel cours ?" valeur={choix} testid="champ-nouveau-cours"
        options={options} onChange={setChoix}
        aide={suivis.length ? "Les cours qu'il suit déjà sont en tête de liste." : undefined} />
      <ExplicationChangement choix={choix} cours={cours} />
      {versUnCours && (
        <div className="mb-4" data-testid="changement-depuis">
          <span className="block text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)] mb-1.5">Depuis quand ?</span>
          <Puces valeur={depuis} onChange={setDepuis} testid="changement-depuis-choix"
            options={[{ valeur: "aujourdhui", label: "À partir d'aujourd'hui", testid: "changement-depuis-aujourdhui" },
              { valeur: "debut", label: "Depuis le début (inscrit par erreur)", testid: "changement-depuis-debut" }]} />
          <p className="text-xs text-[var(--muted)] mt-2" data-testid="changement-depuis-explication">
            {depuis === "debut"
              ? "Il n'a jamais été dans l'ancien cours : les séances passées comptent dans le nouveau. Si la paie d'un prof est déjà validée, la différence t'attend dans Profs et « À traiter », à reporter d'un tap."
              : "Les séances déjà données restent dans l'ancien cours (feuille d'appel, paie du prof)."}
          </p>
        </div>
      )}
      {encoreSuivie(inscription) && (
        <p className="text-xs mb-4" style={{ color: "#B45309" }} data-testid="changement-desinscrit">
          {eleve.prenom} reste désinscrit·e : il ou elle suit le nouveau cours jusqu'au{" "}
          {String(inscription.place_conservee_jusqu_au).slice(0, 10).split("-").reverse().join(".")},
          puis s'arrête comme prévu. S'il ou elle continue ensuite, annule d'abord la désinscription
          avec « Modifier » (statut « Inscrit »).
        </p>
      )}
      {resultat && (
        <p className="text-sm mb-3" style={{ color: "#15803D" }} data-testid="changement-resultat">{resultat.message}</p>
      )}
      {etat.err && <p className="text-sm mb-3" style={{ color: "#B91C1C" }}>{etat.err}</p>}
      <div className="flex gap-2">
        <button className="btn-ghost" onClick={onFermer}>Fermer</button>
        <BoutonSombre onClick={changer} disabled={etat.envoi || !choix || !!resultat} testid="changement-enregistrer">
          <ArrowLeftRight size={14} /> Changer de cours
        </BoutonSombre>
      </div>
    </Modale>
  );
}

function FormulaireAjout({ eleve, onFermer, onOk }) {
  const [cours, setCours] = useState([]);
  const [f, setF] = useState({ cours_id: "", notes: "" });
  const [etat, lancer] = useEnvoi();
  const [message, setMessage] = useState("");

  // Le semestre et la saison EN COURS selon le serveur (calendrier des Réglages) : ils
  // partent explicitement avec l'inscription, au lieu d'être devinés d'après le mois.
  const [periode, setPeriode] = useState({ semestre: "", saison: "" });

  useEffect(() => { api("/liste/cours").then((d) => setCours(d.items)).catch(() => {}); }, []);
  useEffect(() => {
    api("/saisons").then((r) => setPeriode({
      semestre: r.semestre_courant ? String(r.semestre_courant) : "", saison: r.courante || "",
    })).catch(() => {});
  }, []);

  const choisi = cours.find((c) => c.id === f.cours_id);
  const ajouter = () => lancer(async () => {
    const r = await api(`/eleves/${encodeURIComponent(eleve.id)}/inscriptions`, {
      method: "POST",
      body: JSON.stringify({ cours_id: f.cours_id, notes: f.notes, ...periode }),
    });
    setMessage(r.message);
    onOk(r.message);
  });

  return (
    <Modale large titre="Ajouter un cours" sous_titre={`${eleve.prenom} ${eleve.nom} — l'inscription part en liste d'attente si le cours est plein.`}
      onFermer={onFermer} testid="modale-ajout-cours">
      <ChampChoix
        label="Cours"
        valeur={f.cours_id}
        options={[{ valeur: "", label: "Choisir un cours…" },
          ...cours.map((c) => ({ valeur: c.id, label: libelleCours(c) }))]}
        onChange={(v) => setF({ ...f, cours_id: v })}
        testid="champ-cours"
      />
      {choisi && (
        <p className="text-xs mb-4" style={{ color: choisi.nb_inscrits >= choisi.places_max ? "#B45309" : "#15803D" }}>
          {choisi.nb_inscrits >= choisi.places_max
            ? "Ce cours est complet : l'inscription sera mise en liste d'attente."
            : `${(choisi.places_max || 0) - choisi.nb_inscrits} place(s) libre(s).`}
        </p>
      )}
      <p className="text-xs text-[var(--muted)] mb-4">
        Le montant n'est pas saisi ici : il est calculé par le moteur de facturation à partir de
        la durée du cours, de l'âge, des réductions d'accumulation et de la réduction famille.
      </p>
      <ChampTexte label="Notes internes" valeur={f.notes} onChange={(v) => setF({ ...f, notes: v })} testid="champ-notes-ajout" />
      {message && <p className="text-sm mb-3" style={{ color: "#15803D" }}>{message}</p>}
      {etat.err && <p className="text-sm mb-3" style={{ color: "#B91C1C" }}>{etat.err}</p>}
      <div className="flex gap-2">
        <button className="btn-ghost" onClick={onFermer}>Annuler</button>
        <BoutonSombre onClick={ajouter} disabled={etat.envoi || !f.cours_id} testid="modale-ajout-enregistrer">
          <Plus size={14} /> Ajouter
        </BoutonSombre>
      </div>
    </Modale>
  );
}
